import React from "react";
import ReactDOM from "react-dom";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LinkIcon from "@mui/icons-material/Link";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import SchoolIcon from "@mui/icons-material/School";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import TuneIcon from "@mui/icons-material/Tune";
import SendIcon from "@mui/icons-material/Send";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "@blocknote/mantine/style.css";
import "@blocknote/core/fonts/inter.css";
import SectionInputsModal2 from "./SectionInputsModal2";
import ImageSourceControl, { imageModeToFlags } from "./ImageSourceControl";
import { ScaledCardPreview } from "./ScaledCardPreview";
import ContentSourcePicker, { formatSourceUrl } from "./ContentSourcePicker";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { denormalizeEditorDeckForPreview } from "./swipeDeckHelpers";
import CheckIcon from "@mui/icons-material/Check";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ImageIcon from "@mui/icons-material/Image";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import {
  postRightworkAPI,
  getRightworkAPI,
} from "../requests/utilities/requests";
import {
  streamOutlineGenerationAPI,
  streamOutlineEditAPI,
} from "../requests/utilities/streamingRequests";
import {
  API_post_learning_generation_job_stream,
  API_get_learning_generation_job,
  API_post_learning_generation_outline_approval,
  API_post_learning_generation_outline_edit_stream,
  API_post_learning_generation_outline_revert,
  API_post_learning_generation_commit,
  API_post_learning_generation_section_regenerate,
  API_post_learning_generation_section_refine,
  API_post_learning_generation_section_revert_asset,
  API_post_learning_generation_swipe_deck_refine,
  API_post_learning_generation_quiz_refine,
  API_post_learning_generation_section_remove_asset,
  API_post_learning_generation_section_payload_update,
  API_get_assistant_searchable_files,
} from "../../../constants";

// Lazy-loaded so the framer-motion + embla-carousel + canvas-confetti
// bundle (~66KB gzipped) only downloads when a manager actually opens
// a deck preview from the studio.
const SwipeDeckPlayerLazy = React.lazy(() =>
  import("../take_training_employee/swipe_deck_player/SwipeDeckPlayer"),
);

const STAGE_PROMPT = "prompt";
const STAGE_OUTLINE = "outline";
const STAGE_FILLING = "filling";
const STAGE_READY = "ready";

const POLL_INTERVAL_MS = 1500;
const STATUS_AWAITING_APPROVAL = "awaiting_approval";
const STATUS_READY = "ready";
const STATUS_COMMITTED = "committed";
const STATUS_FAILED = "failed";

// Hard upper bound on module_index from a streaming event. Backend
// _parse_outline_stream_line already rejects anything >= MAX_MODULES (10),
// but a stale or compromised backend could still ship larger indices —
// without this guard, the pad-loop in the streaming reducers would
// allocate that many empty placeholders and freeze the browser.
const MAX_STREAMED_MODULE_INDEX = 32;
// Hard panic-stop on per-module section accumulation in the streaming
// reducer. Backend caps at MAX_SECTIONS_PER_MODULE (15) at yield time,
// but the FE keeps an independent ceiling so a stale or compromised
// backend can't drive the outline UI into thousands of rows before
// `done` lands with the canonical (capped) outline.
const MAX_STREAMED_SECTIONS_PER_MODULE = 32;
// Dedicated cap for full-LMS track index gating. Backend caps at
// MAX_TRACKS=20 (raised from 12 once the design pivoted to one-track-per-
// role). FE allows a little headroom but pads way fewer placeholder
// track tiles than reusing MAX_STREAMED_MODULE_INDEX would.
const MAX_STREAMED_TRACK_INDEX = 24;

function pause(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Temp identifier used as a React key for streamed-in modules and
// sections before the canonical server-generated UUIDs arrive in the
// `done` event. Falls back to a Math.random() id when crypto.randomUUID
// isn't available (legacy browsers or non-secure contexts).
function tempUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `tmp-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

// The studio mutates `outline` with UI-only fields (status, as_json,
// error, swipe_deck, quiz preview, etc.) during fill. Strip them before
// sending to the approval endpoint — backend validation rejects unknown
// shapes more strictly now, and shipping UI noise back is wasteful.
//
// Full-LMS outlines carry a `tracks` array alongside the module registry;
// each track also has `role_ids` (committed to LearningTrackRoleThrough
// only when the manager explicitly assigns post-commit — see Option 2
// design — but stored on the outline so the FE can show suggestions).
// The backend's `normalize_approved_full_lms_outline` reads `role_names`
// not `role_ids`, so we re-resolve ids → names from the outline's known
// role_names cache (built by the studio on stream-in).
function stripOutlineForApproval(outline, roleNameById) {
  if (!outline) return outline;
  const stripped = {
    target_kind: outline.target_kind,
    name: outline.name,
    modules: (outline.modules || []).map((m) => ({
      uuid: m.uuid,
      name: m.name,
      sections: (m.sections || []).map((s) => ({
        uuid: s.uuid,
        name: s.name,
        brief: s.brief,
        suggest_swipe_deck: !!s.suggest_swipe_deck,
        suggest_quiz: !!s.suggest_quiz,
      })),
    })),
  };
  if (outline.target_kind === "full_lms") {
    // Defensive: filter each track's module_uuids to ones present in
    // the registry AND non-empty, and drop tracks that end up empty.
    // Without this:
    //   - A stale uuid (left over from a module-remove that didn't
    //     scrub track refs, or any state churn that left things out
    //     of sync) would be silently dropped by the backend approval
    //     normalizer.
    //   - A module that lost all its sections (zero-section modules
    //     are dropped server-side too) would still appear here with
    //     its uuid in validModuleUuids, so tracks referencing only
    //     that module would survive the FE filter but vanish at
    //     approval.
    // Both produce a "valid-looking outline disappears" UX. The
    // state-level reconciliation in handleRemoveModule /
    // handleRemoveSection keeps things consistent during normal use;
    // this filter is the belt-and-suspenders boundary check on the
    // FE→BE wire format.
    const validModuleUuids = new Set(
      stripped.modules
        .filter((m) => (m.sections || []).length > 0)
        .map((m) => m.uuid),
    );
    stripped.tracks = (outline.tracks || [])
      .map((t) => ({
        uuid: t.uuid,
        name: t.name,
        // role_names is what the backend approval normalizer reads.
        // Resolve role_ids back to human names via the supplied map;
        // unknown ids are dropped so a stale role doesn't end up in
        // the payload.
        role_names: (t.role_ids || [])
          .map((rid) => roleNameById?.[rid])
          .filter(Boolean),
        module_uuids: (t.module_uuids || []).filter((u) =>
          validModuleUuids.has(u),
        ),
      }))
      .filter((t) => t.module_uuids.length > 0);
  }
  return stripped;
}

const TARGET_KINDS = [
  {
    key: "track",
    label: "New Track",
    description: "A complete program — multiple modules, sections, and quizzes",
    icon: SchoolIcon,
  },
  {
    key: "module",
    label: "New Module",
    description: "A single chapter — sections and quizzes, attachable to any track",
    icon: ViewModuleIcon,
  },
  {
    key: "full_lms",
    label: "Full LMS",
    description:
      "Multiple role-aligned tracks with shared modules across them — from your roles + docs",
    icon: TuneIcon,
  },
];

const stageMeta = [
  { key: STAGE_PROMPT, label: "Prompt" },
  { key: STAGE_OUTLINE, label: "Outline" },
  { key: STAGE_FILLING, label: "Generate" },
  { key: STAGE_READY, label: "Save" },
];

function StageStepper(props) {
  const currentIdx = stageMeta.findIndex((s) => s.key === props.stage);
  return (
    <div className="flex items-center gap-2">
      {stageMeta.map((s, i) => {
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <React.Fragment key={s.key}>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                isActive
                  ? "bg-violet-700 text-white"
                  : isDone
                  ? "bg-violet-100 text-violet-700"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  isActive
                    ? "bg-white text-violet-700"
                    : isDone
                    ? "bg-violet-700 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {isDone ? "✓" : i + 1}
              </span>
              {s.label}
            </div>
            {i < stageMeta.length - 1 && (
              <div className={`w-6 h-px ${isDone ? "bg-violet-300" : "bg-slate-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TargetKindPicker(props) {
  // Full-LMS is the hero option — render it as a single-column row on
  // top spanning the full width. New Track / New Module sit below in
  // a 2-up grid as the secondary "build one piece" choices. Layout
  // decided in render rather than via a config flag on each entry to
  // keep TARGET_KINDS a flat data array.
  const fullLmsKind = TARGET_KINDS.find((k) => k.key === "full_lms");
  const otherKinds = TARGET_KINDS.filter((k) => k.key !== "full_lms");

  const renderTile = (k, extraClassName = "") => {
    const Icon = k.icon;
    const isActive = props.value === k.key;
    return (
      <button
        key={k.key}
        disabled={props.disabled}
        onClick={() => props.onChange(k.key)}
        className={`text-left p-3 rounded-lg border-2 transition-colors ${
          isActive
            ? "border-violet-500 bg-violet-50"
            : "border-slate-200 hover:border-slate-300 bg-white"
        } disabled:opacity-50 ${extraClassName}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <Icon
            className={isActive ? "text-violet-700" : "text-slate-500"}
            style={{ fontSize: 18 }}
          />
          <span
            className={`text-sm font-semibold ${
              isActive ? "text-violet-900" : "text-slate-700"
            }`}
          >
            {k.label}
          </span>
        </div>
        <p className="text-xs text-slate-500 leading-snug">
          {k.description}
        </p>
      </button>
    );
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        What do you want to generate?
      </label>
      <div className="space-y-2">
        {fullLmsKind && renderTile(fullLmsKind, "w-full")}
        <div className="grid grid-cols-2 gap-2">
          {otherKinds.map((k) => renderTile(k))}
        </div>
      </div>
    </div>
  );
}

function RefinePromptInline(props) {
  const [text, setText] = React.useState("");
  const submit = () => {
    if (!text.trim()) return;
    props.onSubmit(text);
    setText("");
  };
  return (
    <div className="flex items-stretch gap-2 p-2 bg-violet-50 border border-violet-200 rounded-lg">
      <input
        autoFocus={props.autoFocus}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={props.placeholder}
        className="flex-1 bg-white border border-slate-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-violet-400"
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape" && props.onCancel) props.onCancel();
        }}
      />
      <button
        className="px-3 py-1.5 bg-violet-700 hover:bg-violet-800 text-white rounded text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 inline-flex items-center gap-1"
        disabled={!text.trim()}
        onClick={submit}
      >
        <AutoAwesomeIcon style={{ fontSize: 14 }} />
        Apply
      </button>
      {props.onCancel && (
        <button
          className="px-2 py-1.5 text-slate-500 hover:text-slate-700 text-sm"
          onClick={props.onCancel}
        >
          Cancel
        </button>
      )}
    </div>
  );
}

function OutlinePromptBar(props) {
  const [text, setText] = React.useState("");
  const hasText = text.trim().length > 0;
  const busy = !!props.busy;
  // Whether to surface the "Rewriting outline…" status note. Distinct
  // from `busy` so the bar can be disabled during initial generation
  // (when nothing's being "rewritten") without showing misleading copy.
  const editing = !!props.editing;
  const canSubmit = hasText && !busy;
  const submit = () => {
    if (!canSubmit) return;
    props.onSubmit(text);
    setText("");
  };
  const placeholder = props.isModuleMode
    ? 'Ask AI to change the module — e.g. "add a section on allergen handling", "make all sections more concise", "add a quiz to every section"'
    : 'Ask AI to change the outline — e.g. "add a module on cleaning protocols", "add swipe decks to all menu sections", "make the wine section more advanced"';
  return (
    <div className="sticky top-0 z-10 bg-slate-50 -mx-8 px-8 py-3">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm transition-all focus-within:border-violet-400 focus-within:ring-4 focus-within:ring-violet-100 hover:border-slate-300">
        <div className="flex items-center gap-3 px-4 py-3">
          <AutoAwesomeIcon
            className="text-violet-500 flex-shrink-0 self-start mt-0.5"
            style={{ fontSize: 18 }}
          />
          <textarea
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={busy}
            placeholder={placeholder}
            className="flex-1 bg-transparent border-0 focus:outline-none resize-none text-sm leading-relaxed placeholder:text-slate-400 disabled:cursor-not-allowed"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button
            className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
              canSubmit
                ? "bg-violet-700 hover:bg-violet-800 text-white shadow-sm"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
            disabled={!canSubmit}
            onClick={submit}
            aria-label="Apply"
          >
            {busy ? (
              <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <SendIcon style={{ fontSize: 16 }} />
            )}
          </button>
        </div>
      </div>
      {editing && (
        <p className="text-xs text-violet-700 mt-2 px-1 flex items-center gap-1.5">
          <span className="w-3 h-3 border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />
          Rewriting outline…
        </p>
      )}
      {!busy && props.warning && (
        <p className="text-xs text-amber-700 mt-2 px-1">{props.warning}</p>
      )}
    </div>
  );
}

/**
 * Inline reference-material affordance: shows the manager's current
 * selection as removable chips and an "+ Add reference" button that
 * opens the picker modal. Selection state lives in the parent so the
 * modal can be closed and re-opened without losing what was picked.
 */
function ReferencePickerTrigger(props) {
  const {
    selectedFileUuids,
    selectedFolderUuids,
    selectedModuleUuids,
    selectedSectionUuids,
    files,
    folders,
    modules,
    sections,
    onOpenPicker,
    onRemoveFile,
    onRemoveFolder,
    onRemoveModule,
    onRemoveSection,
    isLoading,
    disabled,
  } = props;

  const fileByUuid = React.useMemo(() => {
    const m = new Map();
    (files || []).forEach((f) => m.set(f.uuid, f));
    return m;
  }, [files]);
  const folderByUuid = React.useMemo(() => {
    const m = new Map();
    (folders || []).forEach((f) => m.set(f.uuid, f));
    return m;
  }, [folders]);

  const moduleByUuid = React.useMemo(() => {
    const m = new Map();
    (modules || []).forEach((x) => m.set(x.uuid, x));
    return m;
  }, [modules]);
  const sectionByUuid = React.useMemo(() => {
    const m = new Map();
    (sections || []).forEach((x) => m.set(x.uuid, x));
    return m;
  }, [sections]);

  const selectedFolders = (selectedFolderUuids || [])
    .map((u) => folderByUuid.get(u))
    .filter(Boolean);
  const selectedFiles = (selectedFileUuids || [])
    .map((u) => fileByUuid.get(u))
    .filter(Boolean);
  const selectedModules = (selectedModuleUuids || [])
    .map((u) => moduleByUuid.get(u))
    .filter(Boolean);
  const selectedSections = (selectedSectionUuids || [])
    .map((u) => sectionByUuid.get(u))
    .filter(Boolean);
  const totalSelected =
    selectedFolders.length
    + selectedFiles.length
    + selectedModules.length
    + selectedSections.length;

  return (
    <div className={`space-y-2 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          Reference materials
        </label>
        <span className="text-xs text-slate-500">
          {totalSelected === 0 ? "None selected" : `${totalSelected} selected`}
        </span>
      </div>

      {totalSelected > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedFolders.map((folder) => (
            <span
              key={`folder-${folder.uuid}`}
              className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1"
            >
              <FolderOutlinedIcon style={{ fontSize: 13 }} className="text-amber-600" />
              <span className="font-medium">{folder.name}</span>
              <span className="text-amber-700/70">
                · {folder.file_count} file{folder.file_count === 1 ? "" : "s"}
              </span>
              <button
                onClick={() => onRemoveFolder(folder.uuid)}
                className="ml-0.5 text-amber-700 hover:text-amber-900"
                aria-label={`Remove ${folder.name}`}
              >
                <CloseIcon style={{ fontSize: 12 }} />
              </button>
            </span>
          ))}
          {selectedFiles.map((file) => {
            const sourceUrl = formatSourceUrl(file.source_url);
            const label = sourceUrl || file.name;
            return (
              <span
                key={`file-${file.uuid}`}
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 text-slate-700 rounded px-2 py-1"
              >
                {sourceUrl ? (
                  <LinkIcon style={{ fontSize: 13 }} className="text-slate-500" />
                ) : (
                  <DescriptionOutlinedIcon style={{ fontSize: 13 }} className="text-slate-500" />
                )}
                <span className="font-medium">{label}</span>
                <button
                  onClick={() => onRemoveFile(file.uuid)}
                  className="ml-0.5 text-slate-500 hover:text-slate-700"
                  aria-label={`Remove ${label}`}
                >
                  <CloseIcon style={{ fontSize: 12 }} />
                </button>
              </span>
            );
          })}
          {selectedModules.map((mod) => (
            <span
              key={`module-${mod.uuid}`}
              className="inline-flex items-center gap-1.5 text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded px-2 py-1"
            >
              <ViewModuleIcon style={{ fontSize: 13 }} className="text-violet-600" />
              <span className="font-medium">{mod.name}</span>
              <button
                onClick={() => onRemoveModule(mod.uuid)}
                className="ml-0.5 text-violet-700 hover:text-violet-900"
                aria-label={`Remove ${mod.name}`}
              >
                <CloseIcon style={{ fontSize: 12 }} />
              </button>
            </span>
          ))}
          {selectedSections.map((sec) => (
            <span
              key={`section-${sec.uuid}`}
              className="inline-flex items-center gap-1.5 text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded px-2 py-1"
            >
              <ViewHeadlineIcon style={{ fontSize: 13 }} className="text-violet-600" />
              <span className="font-medium">{sec.name}</span>
              <button
                onClick={() => onRemoveSection(sec.uuid)}
                className="ml-0.5 text-violet-700 hover:text-violet-900"
                aria-label={`Remove ${sec.name}`}
              >
                <CloseIcon style={{ fontSize: 12 }} />
              </button>
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={onOpenPicker}
        disabled={isLoading}
        className="w-full px-3 py-2 border border-dashed border-slate-300 hover:border-violet-400 hover:bg-violet-50 text-violet-700 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-wait"
      >
        <AddIcon style={{ fontSize: 16 }} />
        {isLoading ? "Loading reference materials…" : "Add reference materials"}
      </button>
      <p className="text-xs text-slate-500">
        Pick Company Files files/folders or existing LMS modules and
        sections to ground the generation.
      </p>
    </div>
  );
}

/**
 * Inline roles picker for full-LMS mode. Lets the manager pick which
 * company roles the AI should plan tracks around. Distinct from
 * `RoleAssignmentChips` (used to bind roles to a saved track) — this
 * picker is purely the planning scope and does NOT trigger any
 * auto-enrollment side effect. Per the v1 design ("Option 2"), the
 * commit step deliberately does not write LearningTrackRoleThrough rows
 * even when the AI suggests role bindings on tracks; that's a separate
 * manual admin action after the LMS is generated.
 *
 * Roles are grouped by `role_type` for legibility (FOH / BOH / ...).
 * Toggle a chip to add or remove a role from the scope.
 */
function LmsRolesPicker(props) {
  const { roles, selectedRoleIds, onChange, disabled } = props;

  // Group {role_type → [role]} for the display. Empty role_types are
  // hidden — same defensive pattern as the backend prompt builder.
  const grouped = React.useMemo(() => {
    const out = new Map();
    (roles || []).forEach((r) => {
      const key = r.role_type || "Other";
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(r);
    });
    // Sort within each group by role_name for stable rendering.
    out.forEach((list) =>
      list.sort((a, b) => a.role_name.localeCompare(b.role_name))
    );
    return Array.from(out.entries());
  }, [roles]);

  const selectedSet = React.useMemo(
    () => new Set(selectedRoleIds || []),
    [selectedRoleIds]
  );

  const toggle = (id) => {
    if (disabled) return;
    if (selectedSet.has(id)) {
      onChange((selectedRoleIds || []).filter((x) => x !== id));
    } else {
      onChange([...(selectedRoleIds || []), id]);
    }
  };

  const selectAll = () => {
    if (disabled) return;
    onChange((roles || []).map((r) => r.id));
  };
  const clearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  const totalCount = (roles || []).length;
  const selectedCount = (selectedRoleIds || []).length;

  return (
    <div className={`space-y-2 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          Roles to include
        </label>
        <span className="text-xs text-slate-500">
          {selectedCount === 0
            ? "None selected"
            : `${selectedCount} of ${totalCount} selected`}
        </span>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <button
          onClick={selectAll}
          className="text-violet-700 hover:text-violet-900 font-medium disabled:text-slate-400"
          disabled={disabled || selectedCount === totalCount}
        >
          Select all
        </button>
        <span className="text-slate-300">·</span>
        <button
          onClick={clearAll}
          className="text-slate-500 hover:text-slate-700 disabled:text-slate-300"
          disabled={disabled || selectedCount === 0}
        >
          Clear
        </button>
      </div>

      {grouped.length === 0 && (
        <p className="text-xs text-slate-500 italic">
          No roles found. Add roles for the company first.
        </p>
      )}

      <div className="space-y-3">
        {grouped.map(([roleType, list]) => (
          <div key={roleType}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              {roleType}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {list.map((r) => {
                const isSelected = selectedSet.has(r.id);
                return (
                  <button
                    key={r.id}
                    onClick={() => toggle(r.id)}
                    className={`text-xs px-2 py-1 rounded border transition-colors ${
                      isSelected
                        ? "bg-violet-700 border-violet-700 text-white"
                        : "bg-white border-slate-200 text-slate-700 hover:border-violet-300"
                    }`}
                  >
                    {r.role_name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-500">
        The AI plans tracks around these roles. It will not auto-assign
        anyone — you can attach roles to each track after generation.
      </p>
    </div>
  );
}


/**
 * Slice-3 MVP rendering of the multi-track outline. Read-only summary
 * panel — shows each track with its AI-suggested role bindings (chips)
 * and the modules it references (clickable links that scroll the
 * module registry below into view). Drag-drop / module reassignment
 * lives in slice 4.
 *
 * "Used in N tracks" badges on shared modules are inferred from the
 * tracks data: any module_uuid that appears in 2+ tracks' module_uuids
 * is flagged here so the manager can see at a glance which content is
 * shared.
 */
function FullLmsTracksPanel({ tracks, modules, roleNameById }) {
  // Build a quick lookup so we can render module names by uuid.
  const moduleByUuid = React.useMemo(() => {
    const m = new Map();
    (modules || []).forEach((mod) => m.set(mod.uuid, mod));
    return m;
  }, [modules]);

  // Compute module reuse counts across tracks. Used purely for the
  // "shared with N more" pill on each track row's module entries.
  const reuseCountByUuid = React.useMemo(() => {
    const counts = new Map();
    (tracks || []).forEach((t) => {
      (t.module_uuids || []).forEach((mu) => {
        counts.set(mu, (counts.get(mu) || 0) + 1);
      });
    });
    return counts;
  }, [tracks]);

  const scrollToModule = (uuid) => {
    const el = document.querySelector(`[data-module-uuid="${uuid}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-3 mb-6">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
          Tracks ({tracks.length})
        </h2>
        <p className="text-xs text-slate-500">
          AI suggests which roles each track is for · review and adjust after generation
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {tracks.map((t) => {
          // Role chips: prefer role_ids (canonical post-`done`) but
          // fall back to role_names (live-stream pre-`done` when ids
          // haven't been resolved yet).
          const roleLabels = (t.role_ids || []).length
            ? (t.role_ids || [])
                .map((rid) => roleNameById?.[rid])
                .filter(Boolean)
            : t.role_names || [];

          // module_uuids is the canonical post-`done` shape; pre-`done`
          // tracks carry only module_indices (integers). For the live
          // preview, we show "N modules" placeholder text until the
          // canonical outline arrives.
          const moduleUuids = t.module_uuids || [];
          const liveModuleCount =
            moduleUuids.length || (t.module_indices?.length || 0);

          return (
            <div
              key={t.uuid}
              className="rounded-lg border border-slate-200 bg-white overflow-hidden"
            >
              {/* AI-generated cover hero. Populates progressively from
                  the fill task (each image takes ~30s in parallel). While
                  the cover is still rendering, show a subtle shimmer so
                  the manager knows something's coming — same visual idiom
                  as section "Generating…" badges. */}
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
                {t.cover?.url ? (
                  <img
                    src={t.cover.url}
                    alt={t.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                    onError={(e) => {
                      // Hide a broken/expired signed URL; the shimmer
                      // background renders through.
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-slate-200 to-slate-100 animate-pulse" />
                )}
              </div>
              <div className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-900 leading-snug">
                  {t.name || (
                    <span className="text-slate-400 italic">Unnamed track</span>
                  )}
                </h3>
                <span className="text-xs text-slate-500 flex-shrink-0">
                  {liveModuleCount} module{liveModuleCount === 1 ? "" : "s"}
                </span>
              </div>

              {roleLabels.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {roleLabels.map((label, idx) => (
                    <span
                      key={`${t.uuid}-role-${idx}`}
                      className="inline-flex items-center text-[11px] bg-violet-50 border border-violet-200 text-violet-900 rounded px-1.5 py-0.5"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-slate-400 italic">
                  No roles suggested — assign after save
                </div>
              )}

              {moduleUuids.length > 0 ? (
                <ul className="space-y-1">
                  {moduleUuids.map((mu) => {
                    const mod = moduleByUuid.get(mu);
                    const reuse = reuseCountByUuid.get(mu) || 0;
                    return (
                      <li key={`${t.uuid}-mod-${mu}`}>
                        <button
                          onClick={() => scrollToModule(mu)}
                          className="text-left w-full text-xs text-slate-700 hover:text-violet-700 hover:underline truncate flex items-center gap-1.5"
                        >
                          <span className="truncate">
                            {mod?.name || (
                              <span className="text-slate-400 italic">
                                (missing)
                              </span>
                            )}
                          </span>
                          {reuse > 1 && (
                            <span className="flex-shrink-0 text-[10px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-1 py-0.5">
                              shared {reuse}×
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : liveModuleCount > 0 ? (
                <div className="text-[11px] text-slate-400 italic">
                  Resolving module references…
                </div>
              ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContextSummary(props) {
  const folderUuids = props.folderUuids || [];
  const fileUuids = props.fileUuids || [];
  const moduleUuids = props.moduleUuids || [];
  const sectionUuids = props.sectionUuids || [];
  const totalRefs =
    folderUuids.length
    + fileUuids.length
    + moduleUuids.length
    + sectionUuids.length;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Generating
        </span>
        <span className="text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-0.5">
          {props.targetKind === "track"
            ? "New Track"
            : props.targetKind === "module"
            ? "New Module"
            : "Full LMS"}
        </span>
      </div>
      {/* For track/module the manager's prompt IS the planning anchor and
          renders as the primary body; for full_lms the prompt is optional
          (roles + references carry the load) so we skip it when empty. */}
      {props.prompt && (
        <p className="text-sm text-slate-700 line-clamp-3">{props.prompt}</p>
      )}
      {props.targetKind === "full_lms" && (props.roleIds || []).length > 0 && (
        <div className="border-t border-slate-200 pt-3">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Roles ({(props.roleIds || []).length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(props.roleIds || []).map((rid) => {
              const name = props.roleNameById?.[rid];
              if (!name) return null;
              return (
                <span
                  key={`role-${rid}`}
                  className="inline-flex items-center text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded px-2 py-1"
                >
                  {name}
                </span>
              );
            })}
          </div>
        </div>
      )}
      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
          References ({totalRefs})
        </div>
        {totalRefs === 0 ? (
          <p className="text-xs text-slate-500">No reference materials selected</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {folderUuids.map((uuid) => {
              const f = (props.folders || []).find((x) => x.uuid === uuid);
              if (!f) return null;
              return (
                <span
                  key={`fld-${uuid}`}
                  className="inline-flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1"
                >
                  <FolderOutlinedIcon style={{ fontSize: 12 }} className="text-amber-600" />
                  {f.name}
                  <span className="text-amber-700/70">
                    · {f.file_count}
                  </span>
                </span>
              );
            })}
            {fileUuids.map((uuid) => {
              const f = (props.files || []).find((x) => x.uuid === uuid);
              if (!f) return null;
              const sourceUrl = formatSourceUrl(f.source_url);
              return (
                <span
                  key={uuid}
                  className="inline-flex items-center gap-1 text-xs bg-white border border-slate-200 text-slate-700 rounded px-2 py-1"
                >
                  {sourceUrl ? (
                    <LinkIcon style={{ fontSize: 12 }} className="text-slate-500" />
                  ) : (
                    <DescriptionOutlinedIcon style={{ fontSize: 12 }} />
                  )}
                  {sourceUrl || f.name}
                </span>
              );
            })}
            {moduleUuids.map((uuid) => {
              const m = (props.modules || []).find((x) => x.uuid === uuid);
              if (!m) return null;
              return (
                <span
                  key={`mod-${uuid}`}
                  className="inline-flex items-center gap-1 text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded px-2 py-1"
                >
                  <ViewModuleIcon style={{ fontSize: 12 }} className="text-violet-600" />
                  {m.name}
                </span>
              );
            })}
            {sectionUuids.map((uuid) => {
              const s = (props.sections || []).find((x) => x.uuid === uuid);
              if (!s) return null;
              return (
                <span
                  key={`sec-${uuid}`}
                  className="inline-flex items-center gap-1 text-xs bg-violet-50 border border-violet-200 text-violet-900 rounded px-2 py-1"
                >
                  <ViewHeadlineIcon style={{ fontSize: 12 }} className="text-violet-600" />
                  {s.name}
                </span>
              );
            })}
          </div>
        )}
      </div>
      <button
        className="w-full text-xs text-violet-700 hover:text-violet-800 font-medium border-t border-slate-200 pt-2 disabled:text-slate-400"
        onClick={props.onEdit}
        disabled={props.disabled}
      >
        Edit prompt and references
      </button>
    </div>
  );
}

function ProgressCard(props) {
  const total = props.sections.length;
  const done = props.sections.filter((s) => s.status === "ready").length;
  const erroring = props.sections.filter((s) => s.status === "error").length;
  const generating = props.sections.find((s) => s.status === "generating");
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  // Sections can all be "ready" while the backend job is still FILLING:
  // image generation runs in parallel and the job only flips to READY
  // (enabling Save) once ALL images finish too — covers, swipe-card images,
  // AND section illustrations (see training_generation.py — "All AI images
  // are generated DURING the fill phase" / "READY flip is owned by the
  // finally block"). Without a distinct state here the card said "Generation
  // complete" the instant sections finished, so the image wait looked like a
  // frozen / broken Save button. `finalizing` is passed true while stage is
  // still FILLING.
  const isFinalizing = props.finalizing && done === total && total > 0;
  const headline = isFinalizing
    ? props.imagesExpected
      ? "Finalizing images…"
      : "Finalizing…"
    : total > 0 && done === total
    ? "Generation complete"
    : "Generating content";

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 space-y-3">
      <div className="flex items-center gap-2">
        {isFinalizing ? (
          <div className="w-[18px] h-[18px] border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />
        ) : (
          <AutoAwesomeIcon className="text-violet-700" style={{ fontSize: 18 }} />
        )}
        <span className="text-sm font-semibold text-violet-900">
          {headline}
        </span>
      </div>
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs text-violet-900">
          <span>{done} of {total} sections</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 bg-violet-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-violet-700 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      {generating && (
        <p className="text-xs text-violet-800">
          Working on: <span className="font-medium">{generating.name}</span>
        </p>
      )}
      {erroring > 0 && (
        <p className="text-xs text-rose-700">
          {erroring} section{erroring > 1 ? "s" : ""} failed — expand to retry
        </p>
      )}
      {done < total && (
        <p className="text-[11px] text-violet-700/80 leading-snug">
          Full generation can take up to 20 minutes depending on track length
          and reference material.
        </p>
      )}
      {isFinalizing && (
        <p className="text-[11px] text-violet-700/80 leading-snug">
          {props.imagesExpected
            ? props.coversExpected
              ? "All sections are done. Generating images (covers, swipe cards, and illustrations) — Save unlocks in a moment."
              : "All sections are done. Generating images (swipe cards and illustrations) — Save unlocks in a moment."
            : "All sections are done. Wrapping up — Save unlocks in a moment."}
        </p>
      )}
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Real preview components (replace earlier skeleton stubs)
//
// These render the actual generated payload (BlockNote content, swipe
// cards, quiz questions) so authors can verify what the AI produced
// before committing. AI-fill output is plain BlockNote (paragraph /
// heading / list / table types plus system-inserted image blocks — see
// SECTION_SYSTEM_PROMPT in training_generator.py; tables are shape-
// validated server-side by normalize_table_block before they reach the
// payload); no custom interactive blocks, so the editor uses the
// default schema. Sub-assets (swipe deck, quiz) are stored as separate
// fields on the section, not embedded in as_json.

function BlocksPreview({ asJson }) {
  // Per-section editor instance; only created when the section is
  // expanded (parent's `{expanded && ...}` gate). One editor per
  // expanded section is fine — typical user has 1-2 expanded at once.
  // Falls back to a single empty paragraph if as_json is empty so
  // BlockNote doesn't choke on an empty initialContent array.
  const initial =
    Array.isArray(asJson) && asJson.length > 0
      ? asJson
      : [{ type: "paragraph", content: [] }];
  const editor = useCreateBlockNote({ initialContent: initial });

  // Re-sync content when as_json changes mid-expansion (e.g. after a
  // regenerate). Without this, the editor sticks with its initial
  // snapshot and the user wouldn't see the new content until they
  // collapse + re-expand.
  React.useEffect(() => {
    if (!editor) return;
    const next =
      Array.isArray(asJson) && asJson.length > 0
        ? asJson
        : [{ type: "paragraph", content: [] }];
    try {
      editor.replaceBlocks(editor.document, next);
    } catch (err) {
      // BlockNote occasionally rejects replaceBlocks if the document
      // tree is mid-render. Silent fallback — the next expand will
      // pick up the right content via initialContent.
      console.warn("BlocksPreview replaceBlocks failed", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asJson]);

  if (!asJson || asJson.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded p-3 text-xs text-slate-400 italic">
        No content yet.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded overflow-hidden pt-6 ai-blocks-preview">
      <BlockNoteView editor={editor} editable={false} theme="light" />
    </div>
  );
}

// Mini horizontal strip of card tiles. Card-type-aware: image_text
// shows headline + body, poll/quiz show question + first 2 choices,
// video shows title + caption. Doesn't try to mirror the full player —
// just enough that the author can scan the deck shape.
const CARD_TYPE_BADGE = {
  image_text: { label: "Image + Text", color: "bg-violet-100 text-violet-700", Icon: ImageIcon },
  video: { label: "Video", color: "bg-blue-100 text-blue-700", Icon: OndemandVideoIcon },
  poll: { label: "Poll", color: "bg-amber-100 text-amber-700", Icon: PollOutlinedIcon },
  quiz: { label: "Trivia", color: "bg-emerald-100 text-emerald-700", Icon: QuestionAnswerOutlinedIcon },
};

function MiniCardTile({ card, index }) {
  const meta = CARD_TYPE_BADGE[card?.card_type] || CARD_TYPE_BADGE.image_text;
  const Icon = meta.Icon;

  // While the fill task is still illustrating, an image_text card has a
  // media_suggestion but no media_url yet — the real card would render as a
  // bare/empty image. Overlay the suggestion as an "image coming"
  // placeholder so the author can tell art is pending (parity with the deck
  // editor's StoryboardTile). Only for image-first cards; trivia/poll render
  // their own content.
  const layout = card?.layout_variant || "full_bleed";
  // Read both shapes: the studio payload carries media_suggestion at the
  // card level, while the persisted/deck-editor shape nests it under
  // metadata. Tolerating both keeps this robust if the payload shape is
  // ever unified to metadata-nested (see the swipe-deck consistency note).
  const mediaSuggestion =
    card?.media_suggestion || card?.metadata?.media_suggestion || null;
  const showImageComing =
    !card?.media_url &&
    Boolean(mediaSuggestion) &&
    card?.card_type === "image_text" &&
    layout !== "text_only";

  // True-to-life preview: render the ACTUAL player card scaled to the tile
  // (shared with the swipe deck editor's storyboard tiles via
  // ScaledCardPreview), instead of a separate simplified layout. What the
  // author sees in this strip now matches the live player — full-bleed
  // cards show as full-bleed, split as split, etc. — so there's no surprise
  // between this preview and the "Preview" tap-through. The 9:16 aspect is
  // required: ScaledCardPreview scales a 9:16 player card by width, so the
  // tile must share that aspect to avoid clipping/letterboxing.
  //
  // The type badge + index live in a header row ABOVE the card, not
  // overlaid on it — overlaying covered card content (e.g. a trivia
  // question, which sits at the top of a white card).
  return (
    <div className="flex-shrink-0 w-40">
      <div className="flex items-center justify-between mb-1.5">
        <span className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded ${meta.color}`}>
          <Icon style={{ fontSize: 10 }} />
          {meta.label}
        </span>
        <span className="text-[9px] text-slate-400">#{index + 1}</span>
      </div>
      <div className="relative aspect-[9/16] rounded-lg overflow-hidden bg-black ring-1 ring-slate-200">
        <ScaledCardPreview card={card} />
        {showImageComing && (
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-violet-50 to-violet-100 flex flex-col items-center justify-center gap-1 px-3 text-center">
            <AutoAwesomeIcon className="text-violet-600" style={{ fontSize: 20 }} />
            <p className="text-[9px] text-violet-700 font-semibold uppercase tracking-wide">
              Suggested image
            </p>
            <p className="text-[9px] text-violet-800 italic line-clamp-3 leading-tight">
              {mediaSuggestion}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SwipeDeckPreview({ cards }) {
  if (!Array.isArray(cards) || cards.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded p-3 text-xs text-slate-400 italic">
        No cards generated.
      </div>
    );
  }
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-2">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {cards.map((card, i) => (
          <MiniCardTile key={i} card={card} index={i} />
        ))}
      </div>
    </div>
  );
}

// Quiz preview — list questions with their choices, marking correct
// ones in green. Backend `_normalize_quiz` stores the
// `LearningQuestion`-shaped fields {question, answers: string[],
// answer_indices: number[], answer_choices: [{text}]}, so we derive
// the {text, is_correct} pairs the preview wants from those. We also
// fall back to the legacy `choices` shape so any old in-memory specs
// (or a future LLM passthrough) still render correctly.
function QuizPreview({ quiz }) {
  const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];
  if (questions.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded p-3 text-xs text-slate-400 italic">
        No questions generated.
      </div>
    );
  }
  const choicesFor = (q) => {
    if (Array.isArray(q?.choices) && q.choices.length > 0) return q.choices;
    if (Array.isArray(q?.answers) && q.answers.length > 0) {
      const correctIdx = new Set(
        Array.isArray(q?.answer_indices) ? q.answer_indices : [],
      );
      return q.answers.map((text, i) => ({
        text,
        is_correct: correctIdx.has(i),
      }));
    }
    return [];
  };
  return (
    <div className="bg-white border border-slate-200 rounded divide-y divide-slate-100">
      {questions.map((q, qi) => {
        const choices = choicesFor(q);
        return (
          <div key={qi} className="p-3">
            <div className="text-xs font-semibold text-slate-700 mb-1.5">
              <span className="text-amber-700 mr-1.5">Q{qi + 1}.</span>
              {q?.question || <span className="italic text-slate-400">Untitled</span>}
            </div>
            <div className="space-y-1 ml-1">
              {choices.map((c, ci) => {
                const isCorrect = c?.is_correct === true;
                return (
                  <div
                    key={ci}
                    className={`flex items-start gap-1.5 text-[11px] ${
                      isCorrect ? "text-emerald-700 font-medium" : "text-slate-600"
                    }`}
                  >
                    {isCorrect ? (
                      <CheckIcon
                        style={{ fontSize: 12 }}
                        className="text-emerald-600 mt-0.5 flex-shrink-0"
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        style={{ fontSize: 12 }}
                        className="text-slate-300 mt-0.5 flex-shrink-0"
                      />
                    )}
                    <span className="flex-1">{c?.text || `Option ${ci + 1}`}</span>
                  </div>
                );
              })}
            </div>
            {q?.explanation && (
              <div className="text-[10px] text-slate-500 italic mt-1.5 ml-1 pl-3 border-l-2 border-slate-200">
                {q.explanation}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionRow(props) {
  const {
    section,
    stage,
    expanded,
    refining,
    onToggleExpand,
    onStartRefine,
    onCancelRefine,
    onSubmitRefine,
    submittingSection,
    onRegenerate,
    onEdit,
    onRemove,
    onToggleQuiz,
    onToggleSwipeDeck,
    onRemoveAsset,
    onRevertAsset,
    refiningSwipeDeck,
    refiningQuiz,
    submittingSwipeDeck,
    submittingQuiz,
    onStartRefineSwipeDeck,
    onCancelRefineSwipeDeck,
    onSubmitRefineSwipeDeck,
    onStartRefineQuiz,
    onCancelRefineQuiz,
    onSubmitRefineQuiz,
    onRenameSection,
    onEditSectionBrief,
    onPreviewSwipeDeck,
    dragHandleProps,
    isOutlining,
  } = props;
  const status = section.status || "pending";
  const isOutlineStage = stage === STAGE_OUTLINE;
  // Gate edit affordances during streaming so a manager edit isn't
  // silently overwritten when onDone replaces the in-memory outline
  // with the canonical (server-UUID) payload. Once the stream
  // completes, isOutlining flips false and full editing is restored.
  const editable = isOutlineStage && !isOutlining;

  const statusBadge = () => {
    if (stage === STAGE_OUTLINE) return null;
    if (status === "pending")
      return <span className="text-xs text-slate-400">Queued</span>;
    if (status === "generating")
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-violet-700">
          <span className="w-3 h-3 border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />
          Generating
        </span>
      );
    if (status === "error")
      return (
        <span className="inline-flex items-center gap-1 text-xs text-rose-600">
          <ErrorOutlineIcon style={{ fontSize: 14 }} />
          Failed
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
        <CheckCircleIcon style={{ fontSize: 14 }} />
        Ready
      </span>
    );
  };

  const isReady = status === "ready";
  const isInteractiveSection = stage !== STAGE_OUTLINE && (isReady || status === "error");

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div
        className={`flex items-center gap-3 px-3 py-2.5 ${
          isInteractiveSection ? "cursor-pointer hover:bg-slate-50" : ""
        }`}
        onClick={() => isInteractiveSection && onToggleExpand()}
      >
        {stage === STAGE_OUTLINE && (
          <span
            {...(dragHandleProps || {})}
            // Stop the click from bubbling to the row's expand handler
            // when the manager grabs the handle. The expand handler is
            // gated by isInteractiveSection (false during outline) so
            // it's a no-op today, but cheap insurance.
            onClick={(e) => e.stopPropagation()}
            className={`flex items-center text-slate-400 ${
              dragHandleProps ? "cursor-grab active:cursor-grabbing" : ""
            }`}
          >
            <DragIndicatorIcon style={{ fontSize: 18 }} />
          </span>
        )}
        <div className="flex-1 min-w-0">
          {editable ? (
            // Inline editable fields in the outline stage so authors
            // can rename sections + tweak the brief without leaving
            // the outline. faint always-on underline tells users it's
            // editable; focus turns it violet. onClick stopPropagation
            // so clicking the input doesn't bubble up and toggle the
            // row's expand handler. Brief uses an input (not textarea)
            // for height parity with the previous static text.
            <>
              <input
                type="text"
                value={section.name || ""}
                onChange={(e) => onRenameSection?.(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Section name"
                className="w-full text-sm font-medium text-slate-800 bg-transparent border-0 border-b border-slate-200 focus:border-violet-500 focus:outline-none py-0.5"
              />
              <input
                type="text"
                value={section.brief || ""}
                onChange={(e) => onEditSectionBrief?.(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Describe what this covers"
                className="w-full text-xs text-slate-500 bg-transparent border-0 border-b border-transparent focus:border-violet-300 focus:outline-none py-0.5 mt-0.5"
              />
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-800 truncate">
                {section.name}
              </div>
              <div className="text-xs text-slate-500 truncate">{section.brief}</div>
            </>
          )}
        </div>
        {section.suggest_swipe_deck && (
          <Tooltip title="A swipe deck will be generated and embedded in this section" arrow>
            <span className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded px-1.5 py-0.5">
              <StyleOutlinedIcon style={{ fontSize: 12 }} />
              Swipe Deck
            </span>
          </Tooltip>
        )}
        {section.suggest_quiz && (
          <Tooltip title="A quiz will be generated and embedded in this section" arrow>
            <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">
              <QuizOutlinedIcon style={{ fontSize: 12 }} />
              Quiz
            </span>
          </Tooltip>
        )}
        {statusBadge()}
        {editable && (
          <div className="flex items-center gap-1">
            <Tooltip title={section.suggest_swipe_deck ? "Remove swipe deck" : "Add swipe deck"} arrow>
              <button
                className={`p-1 rounded hover:bg-slate-100 ${
                  section.suggest_swipe_deck ? "text-sky-600" : "text-slate-400"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSwipeDeck();
                }}
              >
                <StyleOutlinedIcon style={{ fontSize: 16 }} />
              </button>
            </Tooltip>
            <Tooltip title={section.suggest_quiz ? "Remove quiz" : "Add quiz"} arrow>
              <button
                className={`p-1 rounded hover:bg-slate-100 ${
                  section.suggest_quiz ? "text-amber-600" : "text-slate-400"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleQuiz();
                }}
              >
                <QuizOutlinedIcon style={{ fontSize: 16 }} />
              </button>
            </Tooltip>
            <Tooltip title="Remove section" arrow>
              <button
                className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <DeleteOutlineIcon style={{ fontSize: 16 }} />
              </button>
            </Tooltip>
          </div>
        )}
        {(status === "ready" || status === "error") && stage !== STAGE_OUTLINE && (
          <div className="flex items-center gap-1">
            {/* Refine button moved inline with "Section content (Blocks)"
                below for consistency with the swipe-deck / quiz pattern.
                Retry stays at the header level — failed sections have no
                preview to anchor it to. */}
            {status === "error" && (
              <Tooltip
                title={
                  stage === STAGE_FILLING
                    ? "Available once all sections finish generating"
                    : "Retry"
                }
                arrow
              >
                <span>
                  <button
                    className="p-1 rounded text-slate-500 enabled:hover:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed"
                    disabled={stage === STAGE_FILLING}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRegenerate();
                    }}
                  >
                    <RefreshIcon style={{ fontSize: 16 }} />
                  </button>
                </span>
              </Tooltip>
            )}
            {/* Opens SectionInputsModal2 over the generated content;
                saves route to the payload-update endpoint (edits live
                in generated_payload_json until Commit). The section's
                quiz / swipe deck are separate payload assets — not
                blocks inside as_json — so they intentionally don't
                appear in the modal; the canvas previews remain the
                place to review them. Disabled while the JOB is still
                filling (mirrors the retry button above): the save
                endpoint only accepts READY/FAILED jobs, so an edit of
                an early-finished section would 409 on save after the
                manager already invested the work. */}
            {isReady && (
              <Tooltip
                title={
                  stage === STAGE_FILLING
                    ? "Available once all sections finish generating"
                    : "Open in section editor"
                }
                arrow
              >
                <span>
                  <button
                    className="p-1 rounded text-slate-500 enabled:hover:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed"
                    disabled={stage === STAGE_FILLING}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                  >
                    <EditOutlinedIcon style={{ fontSize: 16 }} />
                  </button>
                </span>
              </Tooltip>
            )}
            <span className="text-slate-300">
              {expanded ? <ExpandLessIcon style={{ fontSize: 18 }} /> : <ExpandMoreIcon style={{ fontSize: 18 }} />}
            </span>
          </div>
        )}
      </div>
      {(expanded || refining) && isReady && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 space-y-3">
          {expanded && (
            <>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  Section content (Blocks)
                  {submittingSection && (
                    <span className="ml-1 inline-flex items-center gap-1 text-violet-700 normal-case font-normal">
                      <span className="w-3 h-3 border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />
                      Refining…
                    </span>
                  )}
                  {!isOutlineStage && !submittingSection && onStartRefine && (
                    <Tooltip
                      title={
                        stage === STAGE_FILLING
                          ? "Available once all sections finish generating"
                          : "Refine with prompt"
                      }
                      arrow
                    >
                      {/* Span wrapper so MUI Tooltip fires on a disabled
                          button — disabled <button>s swallow pointer events. */}
                      <span>
                        <button
                          className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-violet-700 enabled:hover:bg-violet-100 disabled:text-slate-300 disabled:cursor-not-allowed leading-none"
                          disabled={stage === STAGE_FILLING}
                          aria-label="Refine section content with prompt"
                          onClick={(e) => {
                            e.stopPropagation();
                            onStartRefine();
                          }}
                        >
                          <TuneIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    </Tooltip>
                  )}
                  {!isOutlineStage && !submittingSection && onRevertAsset && section.as_json_previous && (
                    <Tooltip
                      title={
                        stage === STAGE_FILLING
                          ? "Available once all sections finish generating"
                          : "Undo last refine"
                      }
                      arrow
                    >
                      <span>
                        <button
                          className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-violet-700 enabled:hover:bg-violet-100 disabled:text-slate-300 disabled:cursor-not-allowed leading-none"
                          disabled={stage === STAGE_FILLING}
                          aria-label="Undo last content refine"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRevertAsset("content");
                          }}
                        >
                          <UndoIcon style={{ fontSize: 12 }} />
                        </button>
                      </span>
                    </Tooltip>
                  )}
                </div>
                {refining && (
                  <div className="mb-2">
                    <RefinePromptInline
                      autoFocus
                      placeholder="e.g. make this more concise"
                      onSubmit={onSubmitRefine}
                      onCancel={onCancelRefine}
                    />
                  </div>
                )}
                <BlocksPreview asJson={section.as_json} />
              </div>
              {section.suggest_swipe_deck && section.swipe_deck && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-sky-700 uppercase tracking-wider flex items-center gap-1">
                      <StyleOutlinedIcon style={{ fontSize: 12 }} />
                      Embedded swipe deck
                      {Array.isArray(section.swipe_deck.cards) && (
                        <span className="text-sky-600 font-normal normal-case">
                          ({section.swipe_deck.cards.length} card{section.swipe_deck.cards.length === 1 ? "" : "s"})
                        </span>
                      )}
                      {submittingSwipeDeck && (
                        <span className="ml-1 inline-flex items-center gap-1 text-sky-700 normal-case font-normal">
                          <span className="w-3 h-3 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
                          Refining…
                        </span>
                      )}
                      {!isOutlineStage && !submittingSwipeDeck && onStartRefineSwipeDeck && (
                        <Tooltip
                          title={
                            stage === STAGE_FILLING
                              ? "Available once all sections finish generating"
                              : "Refine with prompt"
                          }
                          arrow
                        >
                          <span>
                            <button
                              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-sky-700 enabled:hover:bg-sky-200/60 disabled:text-slate-300 disabled:cursor-not-allowed leading-none"
                              disabled={stage === STAGE_FILLING}
                              aria-label="Refine swipe deck with prompt"
                              onClick={(e) => {
                                e.stopPropagation();
                                onStartRefineSwipeDeck();
                              }}
                            >
                              <TuneIcon style={{ fontSize: 12 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      {!isOutlineStage && !submittingSwipeDeck && onRevertAsset && section.swipe_deck_previous && (
                        <Tooltip
                          title={
                            stage === STAGE_FILLING
                              ? "Available once all sections finish generating"
                              : "Undo last refine"
                          }
                          arrow
                        >
                          <span>
                            <button
                              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-sky-700 enabled:hover:bg-sky-200/60 disabled:text-slate-300 disabled:cursor-not-allowed leading-none"
                              disabled={stage === STAGE_FILLING}
                              aria-label="Undo last swipe deck refine"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRevertAsset("swipe_deck");
                              }}
                            >
                              <UndoIcon style={{ fontSize: 12 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      {!isOutlineStage && !submittingSwipeDeck && onRemoveAsset && (
                        <Tooltip
                          title={
                            stage === STAGE_FILLING
                              ? "Available once all sections finish generating"
                              : "Remove swipe deck"
                          }
                          arrow
                        >
                          <span>
                            <button
                              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-sky-700 enabled:hover:bg-sky-200/60 disabled:text-slate-300 disabled:cursor-not-allowed leading-none"
                              disabled={stage === STAGE_FILLING}
                              aria-label="Remove swipe deck"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveAsset("swipe_deck");
                              }}
                            >
                              <CloseIcon style={{ fontSize: 12 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    {Array.isArray(section.swipe_deck.cards)
                      && section.swipe_deck.cards.length > 0
                      && onPreviewSwipeDeck && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onPreviewSwipeDeck(section.swipe_deck);
                        }}
                        className="text-xs font-medium text-sky-700 hover:text-sky-900 flex items-center gap-1 px-2 py-1 rounded hover:bg-sky-100"
                      >
                        <PlayArrowIcon style={{ fontSize: 14 }} />
                        Preview
                      </button>
                    )}
                  </div>
                  {refiningSwipeDeck && (
                    <div className="mb-2">
                      <RefinePromptInline
                        autoFocus
                        placeholder="e.g. add a poll card"
                        onSubmit={onSubmitRefineSwipeDeck}
                        onCancel={onCancelRefineSwipeDeck}
                      />
                    </div>
                  )}
                  <SwipeDeckPreview cards={section.swipe_deck.cards} />
                </div>
              )}
              {section.suggest_quiz && section.quiz && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider flex items-center gap-1">
                      <QuizOutlinedIcon style={{ fontSize: 12 }} />
                      Embedded quiz
                      {Array.isArray(section.quiz.questions) && (
                        <span className="text-amber-600 font-normal normal-case">
                          ({section.quiz.questions.length} question{section.quiz.questions.length === 1 ? "" : "s"})
                        </span>
                      )}
                      {submittingQuiz && (
                        <span className="ml-1 inline-flex items-center gap-1 text-amber-700 normal-case font-normal">
                          <span className="w-3 h-3 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                          Refining…
                        </span>
                      )}
                      {!isOutlineStage && !submittingQuiz && onStartRefineQuiz && (
                        <Tooltip
                          title={
                            stage === STAGE_FILLING
                              ? "Available once all sections finish generating"
                              : "Refine with prompt"
                          }
                          arrow
                        >
                          <span>
                            <button
                              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-amber-700 enabled:hover:bg-amber-200/60 disabled:text-slate-300 disabled:cursor-not-allowed leading-none"
                              disabled={stage === STAGE_FILLING}
                              aria-label="Refine quiz with prompt"
                              onClick={(e) => {
                                e.stopPropagation();
                                onStartRefineQuiz();
                              }}
                            >
                              <TuneIcon style={{ fontSize: 12 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      {!isOutlineStage && !submittingQuiz && onRevertAsset && section.quiz_previous && (
                        <Tooltip
                          title={
                            stage === STAGE_FILLING
                              ? "Available once all sections finish generating"
                              : "Undo last refine"
                          }
                          arrow
                        >
                          <span>
                            <button
                              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-amber-700 enabled:hover:bg-amber-200/60 disabled:text-slate-300 disabled:cursor-not-allowed leading-none"
                              disabled={stage === STAGE_FILLING}
                              aria-label="Undo last quiz refine"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRevertAsset("quiz");
                              }}
                            >
                              <UndoIcon style={{ fontSize: 12 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      {!isOutlineStage && !submittingQuiz && onRemoveAsset && (
                        <Tooltip
                          title={
                            stage === STAGE_FILLING
                              ? "Available once all sections finish generating"
                              : "Remove quiz"
                          }
                          arrow
                        >
                          <span>
                            <button
                              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-amber-700 enabled:hover:bg-amber-200/60 disabled:text-slate-300 disabled:cursor-not-allowed leading-none"
                              disabled={stage === STAGE_FILLING}
                              aria-label="Remove quiz"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveAsset("quiz");
                              }}
                            >
                              <CloseIcon style={{ fontSize: 12 }} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  {refiningQuiz && (
                    <div className="mb-2">
                      <RefinePromptInline
                        autoFocus
                        placeholder="e.g. make these harder"
                        onSubmit={onSubmitRefineQuiz}
                        onCancel={onCancelRefineQuiz}
                      />
                    </div>
                  )}
                  <QuizPreview quiz={section.quiz} />
                </div>
              )}
            </>
          )}
        </div>
      )}
      {expanded && status === "error" && (
        <div className="border-t border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold mb-1">Generation failed</div>
              <div className="text-rose-600 font-normal break-words">
                {section.error || "The model returned invalid output."}
              </div>
            </div>
            {/* Retry is only available once the bulk fill is done.
                Running fill_section in parallel with the still-active
                Celery filler would race them on job.generated_payload_json
                — the backend rejects with 409 in that case, but disabling
                preemptively is cleaner UX than a post-click toast. */}
            <Tooltip
              title={
                stage === STAGE_FILLING
                  ? "Available once the rest of the sections finish"
                  : ""
              }
              arrow
              disableHoverListener={stage !== STAGE_FILLING}
            >
              <span className="shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRegenerate();
                  }}
                  disabled={stage === STAGE_FILLING}
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded font-medium text-xs flex items-center gap-1 disabled:bg-rose-300 disabled:cursor-not-allowed"
                >
                  <RefreshIcon style={{ fontSize: 14 }} />
                  Retry
                </button>
              </span>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
}

// Module-refine entry point, shared by ModuleBlock's two layouts:
// icon-only inside the track-mode module header (hover-revealed, like
// the adjacent delete), labeled standalone row in module mode (no
// header exists there to host an icon). Shows the loop's progress
// counter while this module's refine is running.
function ModuleRefineControl({
  submittingModule,
  moduleRefineProgress,
  moduleRefineBusy,
  refiningModule,
  onStartRefineModule,
  labeled,
}) {
  if (submittingModule) {
    return (
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-violet-700">
        <span className="w-3 h-3 border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />
        {moduleRefineProgress
          ? `Refining section ${moduleRefineProgress.done + 1} of ${moduleRefineProgress.total}…`
          : "Refining sections…"}
      </span>
    );
  }
  return (
    <Tooltip
      title={
        moduleRefineBusy
          ? "Another module is refining — one at a time"
          : "Refine every section in this module with a prompt"
      }
      arrow
    >
      {/* Span wrapper so the Tooltip fires on a disabled button. */}
      <span className="flex-shrink-0">
        {labeled ? (
          <button
            className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 border border-violet-200 rounded-lg px-2.5 py-1.5 enabled:hover:bg-violet-50 disabled:text-slate-300 disabled:border-slate-200 disabled:cursor-not-allowed"
            disabled={moduleRefineBusy}
            onClick={onStartRefineModule}
          >
            <TuneIcon style={{ fontSize: 14 }} />
            Refine all sections
          </button>
        ) : (
          <button
            className={`p-1 rounded text-violet-700 enabled:hover:bg-violet-50 disabled:text-slate-300 disabled:cursor-not-allowed transition-opacity ${
              refiningModule ? "" : "opacity-0 group-hover:opacity-100"
            }`}
            disabled={moduleRefineBusy}
            onClick={onStartRefineModule}
          >
            <TuneIcon style={{ fontSize: 16 }} />
          </button>
        )}
      </span>
    </Tooltip>
  );
}

function ModuleBlock(props) {
  const {
    module,
    stage,
    isModuleMode,
    expandedSections,
    refiningSectionUuid,
    refiningModule,
    // This module's refine loop is running (shows the inline spinner).
    submittingModule,
    // {done, total} for the running loop, null otherwise.
    moduleRefineProgress,
    // ANY module's loop is running (disables every module Tune button —
    // payload writes race, so one loop at a time).
    moduleRefineBusy,
    onStartRefineModule,
    onCancelRefineModule,
    onSubmitRefineModule,
    onToggleExpandSection,
    onStartRefineSection,
    onCancelRefineSection,
    onSubmitRefineSection,
    onRegenerateSection,
    onEditSection,
    onRemoveSection,
    onToggleQuiz,
    onToggleSwipeDeck,
    onRemoveSectionAsset,
    onRevertSectionAsset,
    submittingSectionForUuid,
    refiningSwipeDeckUuid,
    refiningQuizUuid,
    submittingSwipeDeckForUuid,
    submittingQuizForUuid,
    onStartRefineSwipeDeck,
    onCancelRefineSwipeDeck,
    onSubmitRefineSwipeDeck,
    onStartRefineQuiz,
    onCancelRefineQuiz,
    onSubmitRefineQuiz,
    onRenameModule,
    onRemoveModule,
    onAddSection,
    onRenameSection,
    onEditSectionBrief,
    onPreviewSwipeDeck,
    dragHandleProps,
    isOutlining,
    // Full-LMS-only: tracks that contain this module. Empty for
    // non-full_lms outlines or for modules currently in zero/one
    // track. ModuleBlock renders the sharing affordance only when
    // length > 1 (truly shared) — single-track membership is the
    // implicit default and doesn't need annotation.
    containingTracks,
    // Mirrors the parent's `isDragDisabled` on the Draggable. When
    // true (e.g. full_lms mode — module registry isn't manually
    // reorderable), suppress the drag indicator icon entirely so the
    // UI doesn't dangle a non-functional handle.
    moduleDragDisabled,
  } = props;
  const isShared = (containingTracks || []).length > 1;
  const sectionDragDisabled = !!isOutlining || stage !== STAGE_OUTLINE;
  // Same gate as SectionRow: edit affordances only render once the
  // streaming outline has finished, otherwise mid-stream edits would
  // be wiped by onDone's canonical-outline replacement.
  const editable = stage === STAGE_OUTLINE && !isOutlining;

  return (
    <div className="space-y-2">
      {!isModuleMode && (
        <div className="flex items-center gap-2 group">
          {stage === STAGE_OUTLINE && !moduleDragDisabled && (
            <span
              {...(dragHandleProps || {})}
              className={`flex items-center text-slate-400 ${
                dragHandleProps ? "cursor-grab active:cursor-grabbing" : ""
              }`}
            >
              <DragIndicatorIcon style={{ fontSize: 18 }} />
            </span>
          )}
          <div className="flex-1 min-w-0">
            {editable ? (
              <input
                type="text"
                value={module.name}
                onChange={(e) => onRenameModule(e.target.value)}
                className="w-full text-base font-semibold text-slate-800 bg-transparent border-0 border-b border-transparent focus:border-violet-500 focus:outline-none px-1 py-0.5"
              />
            ) : (
              <span className="text-base font-semibold text-slate-800 px-1">
                {module.name}
              </span>
            )}
            {isShared && (
              <div className="px-1 mt-0.5 text-xs text-amber-700 truncate">
                Used in {containingTracks.length} tracks:{" "}
                <span className="font-medium">
                  {containingTracks
                    .map((t) => t.name || "Unnamed track")
                    .join(", ")}
                </span>
              </div>
            )}
          </div>
          {isShared && (
            <span
              className="flex-shrink-0 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-1.5 py-0.5 font-medium"
              title={`Shared across ${containingTracks.length} tracks — edits affect all of them`}
            >
              Shared {containingTracks.length}×
            </span>
          )}
          {/* Module-level refine: applies one prompt to every finished
              section in the module (sequential loop over the section
              refine endpoint — see handleSubmitRefineModule). READY
              stage only: at the outline stage the whole-outline refine
              prompt already covers structural asks ("split this
              module"), and during filling refines would race the
              filler. */}
          {stage === STAGE_READY && (
            <ModuleRefineControl
              submittingModule={submittingModule}
              moduleRefineProgress={moduleRefineProgress}
              moduleRefineBusy={moduleRefineBusy}
              refiningModule={refiningModule}
              onStartRefineModule={onStartRefineModule}
            />
          )}
          {editable && (
            <button
              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-opacity"
              onClick={onRemoveModule}
            >
              <DeleteOutlineIcon style={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      )}
      {isModuleMode && stage === STAGE_READY && (
        <div className="flex justify-end">
          <ModuleRefineControl
            submittingModule={submittingModule}
            moduleRefineProgress={moduleRefineProgress}
            moduleRefineBusy={moduleRefineBusy}
            refiningModule={refiningModule}
            onStartRefineModule={onStartRefineModule}
            labeled
          />
        </div>
      )}
      {refiningModule && stage === STAGE_READY && (
        <div className={isModuleMode ? "" : "ml-6"}>
          <RefinePromptInline
            autoFocus
            placeholder='e.g. "make every section more casual", "add a concrete example to each section"'
            onSubmit={onSubmitRefineModule}
            onCancel={onCancelRefineModule}
          />
        </div>
      )}
      <div className={isModuleMode ? "space-y-2" : "ml-6 space-y-2"}>
        <Droppable droppableId={`module-${module.uuid}`} type="section">
          {(dropProvided) => (
            <div
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              className="space-y-2"
            >
              {module.sections.map((s, idx) => (
                <Draggable
                  key={s.uuid}
                  draggableId={s.uuid}
                  index={idx}
                  isDragDisabled={sectionDragDisabled}
                >
                  {(dragProvided) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                    >
                      <SectionRow
                        section={s}
                        stage={stage}
                        expanded={expandedSections.includes(s.uuid)}
                        refining={refiningSectionUuid === s.uuid}
                        onToggleExpand={() => onToggleExpandSection(s.uuid)}
                        onStartRefine={() => onStartRefineSection(s.uuid)}
                        onCancelRefine={onCancelRefineSection}
                        onSubmitRefine={(text) => onSubmitRefineSection(s.uuid, text)}
                        onRegenerate={() => onRegenerateSection(s.uuid)}
                        onEdit={() => onEditSection(s.uuid)}
                        onRemove={() => onRemoveSection(s.uuid)}
                        onToggleQuiz={() => onToggleQuiz(s.uuid)}
                        onToggleSwipeDeck={() => onToggleSwipeDeck(s.uuid)}
                        onRemoveAsset={(asset) => onRemoveSectionAsset(s.uuid, asset)}
                        onRevertAsset={(asset) => onRevertSectionAsset(s.uuid, asset)}
                        submittingSection={submittingSectionForUuid === s.uuid}
                        refiningSwipeDeck={refiningSwipeDeckUuid === s.uuid}
                        refiningQuiz={refiningQuizUuid === s.uuid}
                        submittingSwipeDeck={submittingSwipeDeckForUuid === s.uuid}
                        submittingQuiz={submittingQuizForUuid === s.uuid}
                        onStartRefineSwipeDeck={() => onStartRefineSwipeDeck(s.uuid)}
                        onCancelRefineSwipeDeck={onCancelRefineSwipeDeck}
                        onSubmitRefineSwipeDeck={(text) => onSubmitRefineSwipeDeck(s.uuid, text)}
                        onStartRefineQuiz={() => onStartRefineQuiz(s.uuid)}
                        onCancelRefineQuiz={onCancelRefineQuiz}
                        onSubmitRefineQuiz={(text) => onSubmitRefineQuiz(s.uuid, text)}
                        onRenameSection={(name) => onRenameSection(s.uuid, name)}
                        onEditSectionBrief={(brief) => onEditSectionBrief(s.uuid, brief)}
                        onPreviewSwipeDeck={onPreviewSwipeDeck}
                        dragHandleProps={dragProvided.dragHandleProps}
                        isOutlining={isOutlining}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
        {editable && (
          <button
            className="text-xs text-violet-700 hover:text-violet-800 flex items-center gap-1 px-1 py-1 font-medium"
            onClick={onAddSection}
          >
            <AddIcon style={{ fontSize: 14 }} />
            Add section
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyCanvas() {
  return (
    <div className="h-full flex items-center justify-center px-12">
      <div className="max-w-md text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-violet-100 flex items-center justify-center">
          <AutoAwesomeIcon className="text-violet-700" style={{ fontSize: 32 }} />
        </div>
        <h3 className="text-xl font-semibold text-slate-800">
          Describe the training you need
        </h3>
        <p className="text-sm text-slate-600">
          The studio reads your reference materials and proposes a complete
          outline — modules, sections, swipe decks, and quizzes — for you to
          review before any content is generated.
        </p>
        <div className="text-left bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
          <div className="font-semibold text-slate-700">Tips for good prompts</div>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Name the role and the goal ("server onboarding, first 2 weeks")</li>
            <li>Mention length ("5–7 modules") if you have a target</li>
            <li>Reference any compliance requirements explicitly</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function TrackGenerationStudio(props) {
  // Seed the kind picker from the entry point: "Generate with AI" on the New
  // Track / New Module button passes initialTargetKind so the studio opens on
  // the right target. Opened from the AI Studio tab (null) → default "track".
  const [targetKind, setTargetKind] = React.useState(
    props.initialTargetKind || "track",
  );
  const [stage, setStage] = React.useState(STAGE_PROMPT);
  const [prompt, setPrompt] = React.useState("");
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [selectedFolders, setSelectedFolders] = React.useState([]);
  // Existing LMS content picked as source material (uuids). Sent as
  // source_section_uuids / source_module_uuids on job creation; the
  // backend resolves them to text via build_ai_gen_context for outline
  // planning and per-section fills.
  const [selectedLmsSections, setSelectedLmsSections] = React.useState([]);
  const [selectedLmsModules, setSelectedLmsModules] = React.useState([]);
  // Full-LMS-only: ids of company roles the manager picked as the
  // planning scope. Empty in track/module modes (and ignored at submit
  // time). Roles come from props.roles (passed in by
  // CreateTrainingContentDisplay's existing roleNames fetch).
  const [selectedRoleIds, setSelectedRoleIds] = React.useState([]);
  // Prompt-stage image-source control (track covers, swipe-card hero images,
  // section illustrations). Persisted on the job at create time as two flags;
  // the fill task reads them server-side. "photos_ai" = today's default
  // (reuse real photos, AI-generate the rest); "photos" = reuse only; "none".
  const [imageMode, setImageMode] = React.useState("photos_ai");
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [outline, setOutline] = React.useState(null);
  const [outlineName, setOutlineName] = React.useState("");
  const [expandedSections, setExpandedSections] = React.useState([]);
  // Studio's per-section edit modal state. Holds a temp section
  // shape that SectionInputsModal2 can consume; saves route to
  // studioPostLearningSection (payload-update endpoint), not the
  // real section save.
  const [editSectionOpen, setEditSectionOpen] = React.useState(false);
  const [editSectionData, setEditSectionData] = React.useState(null);
  const [isOutlining, setIsOutlining] = React.useState(false);
  const [refiningSectionUuid, setRefiningSectionUuid] = React.useState(null);
  const [submittingSectionForUuid, setSubmittingSectionForUuid] = React.useState(null);
  const [refiningModuleUuid, setRefiningModuleUuid] = React.useState(null);
  // Module whose sequential refine-all-sections loop is in flight.
  // Doubles as a global busy flag: refines write the whole
  // generated_payload_json, so a second module loop running
  // concurrently would race the first's writes.
  const [submittingModuleUuid, setSubmittingModuleUuid] = React.useState(null);
  // {done, total} while the loop runs — drives the "2 of 5" counter in
  // the module header (collapsed section rows show no per-row spinner,
  // so the header is the only progress surface).
  const [moduleRefineProgress, setModuleRefineProgress] = React.useState(null);
  // Per-asset prompt-edit state. `refining*Uuid` is the section currently
  // showing the inline prompt UI; `submitting*ForUuid` is the section
  // whose backend call is in flight (drives the spinner in the preview
  // header). Two flags so the input disappears on submit (so the manager
  // sees their prompt was accepted) while the spinner conveys progress.
  const [refiningSwipeDeckUuid, setRefiningSwipeDeckUuid] = React.useState(null);
  const [submittingSwipeDeckForUuid, setSubmittingSwipeDeckForUuid] = React.useState(null);
  const [refiningQuizUuid, setRefiningQuizUuid] = React.useState(null);
  const [submittingQuizForUuid, setSubmittingQuizForUuid] = React.useState(null);
  const [availableFiles, setAvailableFiles] = React.useState([]);
  const [availableFolders, setAvailableFolders] = React.useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = React.useState(true);
  const [jobUuid, setJobUuid] = React.useState(null);
  // Surfaced when approval succeeded but polling died after that — the
  // backend job is in flight on the server and we offer a Resume button
  // instead of forcing a rewind to STAGE_OUTLINE (which would 409 since
  // the job is past AWAITING_APPROVAL).
  const [pollRetryAvailable, setPollRetryAvailable] = React.useState(false);
  // Toggled by the prompt-edit stream — gates the OutlinePromptBar's busy
  // state and the "Rewriting outline…" indicator. isOutlining stays true
  // throughout the edit (it's the universal "stream in flight" signal), so
  // a separate flag lets us swap the spinner copy without conflating it
  // with the initial-generation path.
  const [isEditingOutline, setIsEditingOutline] = React.useState(false);
  // Mirrors job.outline_revert_available from the most recent edit. Drives
  // the visibility of the "Revert last edit" button — backend keeps a
  // single-step snapshot and clears it on revert, so this flag flips
  // false once the manager undoes.
  const [outlineRevertAvailable, setOutlineRevertAvailable] = React.useState(false);
  const cancelPollRef = React.useRef(false);
  // AbortController for the outline streaming call. Replaced (and the
  // previous one aborted) on every regenerate so a stale stream from a
  // discarded prompt doesn't keep firing setOutline updates.
  const abortStreamRef = React.useRef(null);
  // How many times the studio has auto-retried polling since the last
  // successful fill. Caps the recovery loop so a real outage (backend
  // down) doesn't spin forever — once the cap is hit, the manager
  // sees the manual "Resume polling" button instead.
  const autoResumeAttemptsRef = React.useRef(0);
  // Holds a player-shaped (denormalized) deck while the manager is
  // previewing a generated swipe deck. Null when no preview is open.
  const [previewSwipeDeck, setPreviewSwipeDeck] = React.useState(null);

  const handlePreviewSwipeDeck = React.useCallback((deckSpec) => {
    if (!deckSpec) return;
    setPreviewSwipeDeck(denormalizeEditorDeckForPreview(deckSpec));
  }, []);

  // Callable (not just an effect) so the source picker can re-run it
  // after an in-picker "Add from link" import. The sequence ref drops
  // stale responses: a company switch (or rapid refreshes) mid-flight
  // must not let an older response overwrite a newer one.
  const loadFilesSeqRef = React.useRef(0);
  const loadReferenceFiles = React.useCallback(async () => {
    const seq = ++loadFilesSeqRef.current;
    try {
      const res = await getRightworkAPI(API_get_assistant_searchable_files, {
        company_id: props.homeCompany,
      });
      if (seq !== loadFilesSeqRef.current) return;
      setAvailableFiles(res.data?.redux?.payload?.files || []);
      setAvailableFolders(res.data?.redux?.payload?.folders || []);
    } catch (err) {
      if (seq !== loadFilesSeqRef.current) return;
      const msg =
        err?.response?.data?.system?.message ||
        "Failed to load reference files";
      toast.error(msg, { autoClose: 3000 });
    } finally {
      if (seq === loadFilesSeqRef.current) setIsLoadingFiles(false);
    }
  }, [props.homeCompany]);

  React.useEffect(() => {
    loadReferenceFiles();
    // Invalidate in-flight responses on unmount / company switch — the
    // seq bump makes any late response a no-op (replaces the old
    // closure-`cancelled` guard the callable refactor removed).
    return () => {
      loadFilesSeqRef.current += 1;
    };
  }, [loadReferenceFiles]);

  React.useEffect(() => {
    return () => {
      cancelPollRef.current = true;
      if (abortStreamRef.current) {
        try { abortStreamRef.current.abort(); } catch (_) { /* noop */ }
      }
    };
  }, []);

  const isModuleMode = outline?.target_kind === "module" || (stage === STAGE_PROMPT && targetKind === "module");
  const isFullLmsMode = outline?.target_kind === "full_lms" || (stage === STAGE_PROMPT && targetKind === "full_lms");
  // Single source of truth for "the module list is reorderable right
  // now". Read by the Draggable's `isDragDisabled` AND by ModuleBlock
  // (to suppress the drag-indicator icon when the handle wouldn't
  // function).
  const moduleDragDisabled =
    !!isOutlining ||
    isModuleMode ||
    isFullLmsMode ||
    stage !== STAGE_OUTLINE;

  // Reverse map for `stripOutlineForApproval` to resolve role_ids back
  // to role names (the backend's full-LMS approval normalizer reads
  // role_names rather than ids — kept consistent with the streaming
  // outline event vocab, which sends names). Cheap to derive every
  // render since props.roles is a small flat array.
  const roleNameById = React.useMemo(() => {
    const out = {};
    (props.roles || []).forEach((r) => {
      out[r.id] = r.role_name;
    });
    return out;
  }, [props.roles]);

  // Full-LMS-only: per-module list of tracks that reference it.
  // {moduleUuid: [{uuid, name}, ...]}. Drives the "Used in N tracks"
  // affordances in the module header + (later) the section editor's
  // shared-module banner. Empty Map for non-full_lms outlines — the
  // ModuleBlock conditionally renders nothing when its membership list
  // is empty / has length 1.
  const tracksByModuleUuid = React.useMemo(() => {
    const map = new Map();
    ((outline?.tracks) || []).forEach((t) => {
      (t.module_uuids || []).forEach((mu) => {
        if (!map.has(mu)) map.set(mu, []);
        map.get(mu).push({ uuid: t.uuid, name: t.name });
      });
    });
    return map;
  }, [outline?.tracks]);

  const allSections = React.useMemo(() => {
    if (!outline) return [];
    return outline.modules.flatMap((m) => m.sections);
  }, [outline]);

  const handleToggleFile = (uuid) => {
    setSelectedFiles((prev) =>
      prev.includes(uuid) ? prev.filter((x) => x !== uuid) : [...prev, uuid]
    );
  };
  const handleToggleFolder = (uuid) => {
    setSelectedFolders((prev) =>
      prev.includes(uuid) ? prev.filter((x) => x !== uuid) : [...prev, uuid]
    );
  };
  const handleToggleLmsSection = (uuid) => {
    setSelectedLmsSections((prev) =>
      prev.includes(uuid) ? prev.filter((x) => x !== uuid) : [...prev, uuid]
    );
  };
  const handleToggleLmsModule = (uuid) => {
    setSelectedLmsModules((prev) =>
      prev.includes(uuid) ? prev.filter((x) => x !== uuid) : [...prev, uuid]
    );
  };

  // Expand selected folder UUIDs into the full set of file UUIDs
  // belonging to those folders, then merge with the explicitly-picked
  // file UUIDs (deduped). Used both for submit-time wire payload and
  // for the inline ContextSummary tally.
  const expandedFileUuids = React.useMemo(() => {
    if (selectedFolders.length === 0) return selectedFiles;
    const folderSet = new Set(selectedFolders);
    const fromFolders = availableFiles
      .filter((f) => folderSet.has(f.folder_uuid))
      .map((f) => f.uuid);
    return Array.from(new Set([...selectedFiles, ...fromFolders]));
  }, [selectedFiles, selectedFolders, availableFiles]);

  const pollJobUntil = async (uuidArg, terminalStatuses, onUpdate) => {
    // A transient network blip shouldn't kill the whole polling loop while
    // the backend is happily generating. Tolerate up to 4 consecutive
    // failures (~12s of network unreachable) with linear backoff before
    // giving up. Successful poll resets the counter.
    const MAX_CONSECUTIVE_ERRORS = 4;
    let consecutiveErrors = 0;
    while (!cancelPollRef.current) {
      try {
        const res = await getRightworkAPI(API_get_learning_generation_job, {
          company_id: props.homeCompany,
          job_uuid: uuidArg,
        });
        // Re-check cancellation after every await — the component may have
        // unmounted while the request was in flight, in which case any
        // setState the onUpdate callback fires would warn about updating
        // an unmounted component.
        if (cancelPollRef.current) {
          throw new Error("Polling cancelled");
        }
        const job = res.data?.redux?.payload?.job;
        if (!job) {
          throw new Error("Generation job not found");
        }
        consecutiveErrors = 0;
        if (onUpdate) onUpdate(job);
        if (terminalStatuses.includes(job.status)) {
          return job;
        }
      } catch (err) {
        if (cancelPollRef.current) throw err;
        // 4xx errors mean the backend rejected us — no point retrying.
        const httpStatus = err?.response?.status;
        if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
          throw err;
        }
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          throw err;
        }
      }
      await pause(POLL_INTERVAL_MS * Math.max(1, consecutiveErrors));
    }
    throw new Error("Polling cancelled");
  };

  const handleGenerateOutline = async () => {
    // Track/module require a prompt; full-LMS doesn't (roles + refs are
    // the planning anchors) but does require at least one role.
    if (targetKind !== "full_lms" && !prompt.trim()) {
      toast.error("Please describe the training you want", { autoClose: 3000 });
      return;
    }
    if (targetKind === "full_lms" && selectedRoleIds.length === 0) {
      toast.error("Pick at least one role for the LMS to plan around", {
        autoClose: 3000,
      });
      return;
    }

    // Abort any prior in-flight stream before starting a new one. Without
    // this, a regenerate while a previous stream was still running would
    // race two setOutline pipelines into the same state.
    if (abortStreamRef.current) {
      try { abortStreamRef.current.abort(); } catch (_) { /* noop */ }
    }
    const controller = new AbortController();
    abortStreamRef.current = controller;

    // The fill stage still polls; reset its cancel flag so a previous
    // unmount-then-remount doesn't pre-cancel the upcoming poll loop.
    cancelPollRef.current = false;
    setIsOutlining(true);

    // Flip to the outline stage synchronously, before awaiting the fetch
    // round-trip. The "Generating outline…" pill renders against an
    // empty modules list while the network connects + RAG runs; modules
    // and sections fill in as events arrive. Without this, the manager
    // sees the prompt button's spinner for ~100-300ms before the studio
    // changes view. If the request fails the catch block bounces back.
    // Full-LMS jobs additionally carry an empty `tracks` array — the
    // streamed `track` events populate it later.
    setOutline({
      target_kind: targetKind,
      name: "",
      modules: [],
      ...(targetKind === "full_lms" ? { tracks: [] } : {}),
    });
    setOutlineName("");
    setStage(STAGE_OUTLINE);

    try {
      await streamOutlineGenerationAPI(
        API_post_learning_generation_job_stream,
        {
          company_id: props.homeCompany,
          target_kind: targetKind,
          prompt: prompt.trim(),
          // Folders are expanded client-side into their constituent
          // file UUIDs so the backend protocol stays unchanged. Trade-
          // off: if a file is added to a selected folder later, this
          // generation pass won't include it (acceptable for a one-
          // shot run where the manager picked at this moment).
          source_file_uuids: expandedFileUuids,
          // Existing LMS content picked as source material.
          source_section_uuids: selectedLmsSections,
          source_module_uuids: selectedLmsModules,
          // Full-LMS-only payload field; backend validates that this is
          // non-empty when target_kind=full_lms and ignores it
          // otherwise. Sent unconditionally as []; saves a branch.
          role_ids: targetKind === "full_lms" ? selectedRoleIds : [],
          ...imageModeToFlags(imageMode),
        },
        {
          onStarted: (job) => {
            if (job?.uuid) setJobUuid(job.uuid);
          },
          onProgram: (name) => {
            setOutline((prev) => (prev ? { ...prev, name } : prev));
            setOutlineName(name);
          },
          onModule: (name, moduleIndex) => {
            // Reject out-of-range indices BEFORE the pad loop. Without
            // this, an event with module_index = 1e9 would allocate a
            // billion empty objects and freeze the browser. Backend
            // already rejects indices >= MAX_MODULES, but FE defends
            // independently.
            if (
              !Number.isInteger(moduleIndex) ||
              moduleIndex < 0 ||
              moduleIndex > MAX_STREAMED_MODULE_INDEX
            ) {
              return;
            }
            setOutline((prev) => {
              if (!prev) return prev;
              const modules = [...prev.modules];
              // Pad with empty placeholders if the LLM emits modules
              // out of order (module_index 2 before 1). The backend's
              // assemble_outline_from_events does the same on its side
              // — keep the UI consistent with what'll be persisted.
              while (modules.length <= moduleIndex) {
                modules.push({
                  uuid: tempUuid(),
                  name: "",
                  sections: [],
                });
              }
              modules[moduleIndex] = { ...modules[moduleIndex], name };
              return { ...prev, modules };
            });
          },
          onSection: (section, moduleIndex) => {
            if (
              !Number.isInteger(moduleIndex) ||
              moduleIndex < 0 ||
              moduleIndex > MAX_STREAMED_MODULE_INDEX
            ) {
              return;
            }
            setOutline((prev) => {
              if (!prev) return prev;
              const modules = [...prev.modules];
              while (modules.length <= moduleIndex) {
                modules.push({
                  uuid: tempUuid(),
                  name: "",
                  sections: [],
                });
              }
              const target = modules[moduleIndex];
              // Drop section events past the panic cap. Backend caps at
              // MAX_SECTIONS_PER_MODULE (15); this protects against a
              // stale/compromised backend forwarding more than that
              // before `done` arrives with the canonical outline.
              if (
                (target.sections?.length ?? 0)
                  >= MAX_STREAMED_SECTIONS_PER_MODULE
              ) {
                return prev;
              }
              modules[moduleIndex] = {
                ...target,
                sections: [
                  ...target.sections,
                  {
                    ...section,
                    uuid: tempUuid(),
                    status: "pending",
                  },
                ],
              };
              return { ...prev, modules };
            });
          },
          onTrack: (track, trackIndex) => {
            // Full-LMS-only event. Accumulate streamed tracks into a
            // separate array on outline state. Track references modules
            // by `module_indices` (integer positions in the stream);
            // we keep the integers here for live rendering and let the
            // canonical `done` payload swap in real uuid-bearing
            // module_uuids when the stream finishes.
            if (
              !Number.isInteger(trackIndex) ||
              trackIndex < 0 ||
              trackIndex > MAX_STREAMED_TRACK_INDEX
            ) {
              return;
            }
            setOutline((prev) => {
              if (!prev) return prev;
              const tracks = [...(prev.tracks || [])];
              while (tracks.length <= trackIndex) {
                tracks.push({
                  uuid: tempUuid(),
                  name: "",
                  role_names: [],
                  module_indices: [],
                  module_uuids: [],
                  role_ids: [],
                });
              }
              tracks[trackIndex] = {
                ...tracks[trackIndex],
                name: track.name,
                role_names: track.role_names || [],
                module_indices: track.module_indices || [],
              };
              return { ...prev, tracks };
            });
          },
          onDone: (job) => {
            // Swap in the canonical outline (with server-generated
            // UUIDs) so subsequent approval/regen calls send the right
            // identifiers. Re-tag sections as "pending" the same way
            // the previous polling-based path did. For full-LMS, the
            // canonical outline includes a top-level `tracks` array
            // with uuid-bearing tracks + module_uuids references; the
            // deep-clone preserves it automatically.
            const canonical = job?.outline_json;
            if (!canonical) return;
            const cloned = JSON.parse(JSON.stringify(canonical));
            cloned.modules.forEach((m) =>
              m.sections.forEach((s) => (s.status = "pending"))
            );
            setOutline(cloned);
            setOutlineName(cloned.name);
            // Fresh generation always lands with no prior outline to
            // revert to, but read from the payload anyway to keep this
            // flag in lockstep with the server's view of the job.
            setOutlineRevertAvailable(Boolean(job.outline_revert_available));
          },
          onError: (msg) => {
            // The helper also throws after invoking onError, so the
            // outer catch handles user-facing toasting. Swallow here
            // to avoid double-toasting.
          },
        },
        { signal: controller.signal },
      );
    } catch (err) {
      // Aborts (component unmount or restart-mid-stream) are intentional
      // — no toast. The next handleGenerateOutline call will repopulate
      // state, or the unmount cleanup will discard it.
      if (err?.name === "AbortError") return;
      const msg =
        err?.message ||
        err?.response?.data?.system?.message ||
        "Failed to generate outline";
      toast.error(msg, { autoClose: 4000 });
      // Drop back to the prompt stage so the manager can retry. A
      // partial outline left visible would be worse than starting over,
      // since the backend job is already FAILED.
      setStage(STAGE_PROMPT);
      setOutline(null);
      setJobUuid(null);
    } finally {
      // Only reset state if THIS call still owns the abortRef. On a
      // rapid double-Generate, call 1's finally runs after call 2 has
      // already taken over (call 2 set the ref to its own controller).
      // Without this guard, call 1's setIsOutlining(false) would flip
      // the studio to "Review the outline" while call 2 is still
      // streaming. The catch's stage/outline resets are gated by the
      // AbortError early-return, so they're already safe.
      if (abortStreamRef.current === controller) {
        setIsOutlining(false);
        abortStreamRef.current = null;
      }
    }
  };

  // Polling separated from the approval POST so a transient poll failure
  // can be retried without re-submitting the outline (re-approval would
  // 409 since the backend job is already past AWAITING_APPROVAL).
  const pollAndCompleteFill = async () => {
    // Include COMMITTED so a concurrent tab finalizing the job doesn't
    // strand this poll loop — the backend transitions READY → COMMITTED
    // once any session saves the track, and without it as a terminal
    // status this tab keeps hitting get_learning_generation_job forever
    // while the UI sits in `filling`. Treat it the same as READY since
    // generated_payload_json is preserved across the commit.
    const finalJob = await pollJobUntil(
      jobUuid,
      [STATUS_READY, STATUS_COMMITTED, STATUS_FAILED],
      (job) => mergeGeneratedPayload(job.generated_payload_json),
    );
    if (finalJob.status === STATUS_FAILED) {
      throw new Error(finalJob.error || "Section generation failed");
    }
    mergeGeneratedPayload(finalJob.generated_payload_json);
    setStage(STAGE_READY);
  };

  const handleApproveOutline = async () => {
    if (!jobUuid) {
      toast.error("Generation job lost — please regenerate the outline", {
        autoClose: 4000,
      });
      return;
    }
    // Defense-in-depth alongside the button's disabled gate. Approving
    // while the outliner is still running would 409 against the backend
    // (job still in OUTLINING) and the recovery path would then poll a
    // job that hasn't reached FILLING — leaving the studio stuck.
    if (isOutlining) return;
    cancelPollRef.current = false;
    setPollRetryAvailable(false);
    setStage(STAGE_FILLING);

    let approvalSucceeded = false;
    try {
      // Send the (possibly user-edited) structural outline. UI-only state
      // (status, content, error) is stripped — backend validates and
      // re-normalizes whatever we send.
      const approvalRes = await postRightworkAPI(
        API_post_learning_generation_outline_approval,
        {
          company_id: props.homeCompany,
          job_uuid: jobUuid,
          outline_json: stripOutlineForApproval(outline, roleNameById),
        },
      );
      approvalSucceeded = true;

      // Reconcile local outline with the backend-normalized version
      // before polling starts. `normalize_approved_outline` re-mints
      // UUIDs for any modules or sections the manager added in the UI
      // (s-new-*, m-new-*) — the polling merge matches sections by
      // uuid, so without this sync those rows would never receive
      // status updates and stay stuck "pending" even though the backend
      // job progressed. Resetting status to "pending" here also doubles
      // as the visual "starting fresh" cue.
      const normalizedOutline =
        approvalRes?.data?.redux?.payload?.job?.outline_json;
      if (normalizedOutline && Array.isArray(normalizedOutline.modules)) {
        const cloned = JSON.parse(JSON.stringify(normalizedOutline));
        cloned.modules.forEach((m) =>
          (m.sections || []).forEach((s) => (s.status = "pending"))
        );
        setOutline(cloned);
        setOutlineName(cloned.name);
      }

      await pollAndCompleteFill();
    } catch (err) {
      // Polling cancellation comes from the component unmounting — don't
      // toast or rewind the stage; the studio is going away anyway.
      if (cancelPollRef.current) return;

      // approvalSucceeded only flips true after the response lands, so a
      // local-side failure (timeout, dropped connection, mid-response
      // abort) leaves us unsure whether the server actually processed
      // the request. Probe the job status before deciding the recovery
      // path — if the server is already past AWAITING_APPROVAL, the
      // POST landed and we should reattach via Resume rather than
      // rewinding (where the next Approve would 409).
      let serverPastApproval = approvalSucceeded;
      if (!serverPastApproval && jobUuid) {
        try {
          const probeRes = await getRightworkAPI(
            API_get_learning_generation_job,
            { company_id: props.homeCompany, job_uuid: jobUuid },
          );
          const probeStatus =
            probeRes?.data?.redux?.payload?.job?.status;
          serverPastApproval =
            !!probeStatus && probeStatus !== STATUS_AWAITING_APPROVAL;
        } catch {
          // Probe itself failed (network really is down). Default to
          // rewind — re-approving on a still-AWAITING_APPROVAL job
          // succeeds; on an already-FILLING job it 409s, which surfaces
          // clearly enough for the manager to refresh and recover.
        }
      }

      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        (serverPastApproval
          ? "Polling stopped — backend may still be filling"
          : "Failed to fill sections");
      toast.error(msg, { autoClose: 4000 });
      if (serverPastApproval) {
        // Backend job is already past AWAITING_APPROVAL — re-approving
        // would 409. Stay in STAGE_FILLING and surface a "Resume polling"
        // affordance so the user can re-attach to the in-flight job.
        setPollRetryAvailable(true);
      } else {
        // Approval POST never landed (or we can't tell and the probe
        // failed too). Rewind so the user can retry approve or edit.
        setStage(STAGE_OUTLINE);
      }
    }
  };

  const handleResumePolling = async () => {
    if (!jobUuid) return;
    cancelPollRef.current = false;
    setPollRetryAvailable(false);
    try {
      await pollAndCompleteFill();
      // Successful resume — reset the auto-retry counter so a future
      // unrelated stall starts with a fresh budget.
      autoResumeAttemptsRef.current = 0;
    } catch (err) {
      if (cancelPollRef.current) return;
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        "Polling failed";
      toast.error(msg, { autoClose: 4000 });
      setPollRetryAvailable(true);
    }
  };

  // Auto-resume polling when it stalls. Covers the common case of a
  // transient network blip or backend restart (in dev: Django StatReloader
  // kicking in mid-fill) where the poll loop hit MAX_CONSECUTIVE_ERRORS
  // and bailed, leaving the studio in STAGE_FILLING with a frozen view of
  // section statuses. After MAX_AUTO_RESUME_ATTEMPTS we stop auto-retrying
  // and let the manual "Resume polling" button take over — a sustained
  // outage probably needs manual attention.
  React.useEffect(() => {
    if (!pollRetryAvailable) {
      // Reset the counter once we leave the stalled state — either via
      // a successful auto-resume (handled above) or because the manager
      // navigated away.
      if (stage !== STAGE_FILLING) {
        autoResumeAttemptsRef.current = 0;
      }
      return;
    }
    const MAX_AUTO_RESUME_ATTEMPTS = 2;
    if (autoResumeAttemptsRef.current >= MAX_AUTO_RESUME_ATTEMPTS) {
      return;
    }
    // Small delay so a backend restart has time to come back up. If we
    // fire immediately, the first auto-resume can hit the same TCP
    // errors and burn an attempt.
    const handle = setTimeout(() => {
      autoResumeAttemptsRef.current += 1;
      handleResumePolling();
    }, 3000);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollRetryAvailable, stage]);

  // Apply a backend payload snapshot to the studio's outline state. Reads
  // `status`, `as_json`, `error` per section so partial progress shows as
  // sections complete one at a time during polling.
  const mergeGeneratedPayload = (payload) => {
    if (!payload || !Array.isArray(payload.modules)) return;
    setOutline((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      const sectionByUuid = {};
      payload.modules.forEach((m) =>
        (m.sections || []).forEach((s) => {
          sectionByUuid[s.uuid] = s;
        })
      );
      next.modules.forEach((m) =>
        m.sections.forEach((s) => {
          const fresh = sectionByUuid[s.uuid];
          if (!fresh) return;
          // Backend uses "failed"; the existing studio UI keys off "error".
          if (fresh.status) {
            s.status = fresh.status === "failed" ? "error" : fresh.status;
          }
          // Sync the section name so a rename saved via the per-section
          // Edit modal (payload_update persists payload_section["name"])
          // reflects in the outline list immediately. During fill polling
          // the payload name matches the seeded outline name, so this is a
          // no-op then — it only changes anything after a manual rename.
          if (fresh.name !== undefined) s.name = fresh.name;
          if (fresh.as_json !== undefined) s.as_json = fresh.as_json;
          if (fresh.error !== undefined) s.error = fresh.error;
          if (fresh.swipe_deck !== undefined) s.swipe_deck = fresh.swipe_deck;
          if (fresh.quiz !== undefined) s.quiz = fresh.quiz;
          // Refine-undo snapshots: presence drives the per-asset
          // "Revert" affordance; the revert endpoint nulls them after
          // consuming (single-step undo).
          if (fresh.as_json_previous !== undefined) {
            s.as_json_previous = fresh.as_json_previous;
          }
          if (fresh.swipe_deck_previous !== undefined) {
            s.swipe_deck_previous = fresh.swipe_deck_previous;
          }
          if (fresh.quiz_previous !== undefined) {
            s.quiz_previous = fresh.quiz_previous;
          }
          // Sync the suggest_* flags too — the remove-asset endpoint
          // clears them server-side, and the refine endpoints flip
          // them back to true. Without this, optimistic local
          // mutations could drift permanently out of sync with the
          // server, and managers could commit an asset they thought
          // was removed (or vice versa).
          if (fresh.suggest_swipe_deck !== undefined) {
            s.suggest_swipe_deck = fresh.suggest_swipe_deck;
          }
          if (fresh.suggest_quiz !== undefined) {
            s.suggest_quiz = fresh.suggest_quiz;
          }
        })
      );
      // Merge AI-generated cover images from the payload.
      // - full_lms: covers live on each track in payload.tracks[i].cover
      // - track:    a single cover at payload.cover (program-level)
      // The fill task creates these progressively as image gen completes,
      // so the studio polling reflects covers populating alongside
      // section content.
      if (Array.isArray(payload.tracks) && Array.isArray(next.tracks)) {
        const coverByUuid = {};
        payload.tracks.forEach((t) => {
          if (t && t.uuid && t.cover) coverByUuid[t.uuid] = t.cover;
        });
        next.tracks.forEach((t) => {
          const fresh = coverByUuid[t.uuid];
          if (fresh) t.cover = fresh;
        });
      }
      if (payload.cover && next.target_kind === "track") {
        next.cover = payload.cover;
      }
      return next;
    });
  };

  const updateSectionStatus = (uuid, status) => {
    setOutline((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) =>
        m.sections.forEach((s) => {
          if (s.uuid === uuid) s.status = status;
        })
      );
      return next;
    });
  };

  // Re-runs fill_section synchronously on the backend for one section.
  // The backend returns the full updated job; mergeGeneratedPayload
  // diffs it back into local outline state, so as_json / swipe_deck /
  // quiz / status / error all refresh together. Optimistic local
  // status flip → "generating" gives immediate feedback before the
  // round-trip resolves.
  // While the module loop walks its sections, every other payload-
  // writing action must wait: refines, regenerates, modal saves, and
  // commit all do read-modify-write of the whole generated_payload_json,
  // and interleaving them with the loop can drop a just-landed write.
  // (The backend's conditional persists catch most races with a 409,
  // but payload_update has no per-section soft lock — blocking up
  // front is the honest UX anyway.)
  const moduleRefineInFlightGuard = () => {
    if (!submittingModuleUuid) return false;
    toast.error("Wait for the module refine to finish.", { autoClose: 3000 });
    return true;
  };

  const handleRegenerateSection = async (uuid) => {
    if (moduleRefineInFlightGuard()) return;
    if (!jobUuid) {
      toast.error("Generation job lost — please regenerate the outline", {
        autoClose: 3000,
      });
      return;
    }
    // Defense-in-depth alongside the Retry button's disabled gate. The
    // backend rejects with 409 if the bulk fill is still in flight, but
    // bailing here saves the round-trip and keeps the error surface
    // (toast) for genuinely unexpected failures.
    if (stage === STAGE_FILLING) {
      toast.error(
        "Wait for the other sections to finish before retrying.",
        { autoClose: 3000 },
      );
      return;
    }
    updateSectionStatus(uuid, "generating");
    try {
      const res = await postRightworkAPI(
        API_post_learning_generation_section_regenerate,
        {
          company_id: props.homeCompany,
          job_uuid: jobUuid,
          section_uuid: uuid,
        },
      );
      const job = res?.data?.redux?.payload?.job;
      if (job?.generated_payload_json) {
        mergeGeneratedPayload(job.generated_payload_json);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        "Section regeneration failed";
      toast.error(msg, { autoClose: 4000 });
      // Mark as error so the user can see the failure surfaced in
      // the rail (and retry button stays clickable).
      updateSectionStatus(uuid, "error");
    }
  };

  const handleStartRefineSection = (uuid) => {
    setRefiningSectionUuid(uuid);
    setExpandedSections((prev) => (prev.includes(uuid) ? prev : [...prev, uuid]));
  };

  const handleSubmitRefineSection = async (uuid, instructions) => {
    const trimmed = (instructions || "").trim();
    if (!trimmed) return;
    if (moduleRefineInFlightGuard()) return;
    if (!jobUuid) {
      toast.error("Generation job lost — please regenerate the outline", {
        autoClose: 3000,
      });
      return;
    }
    if (stage === STAGE_FILLING) {
      toast.error(
        "Wait for the other sections to finish before refining.",
        { autoClose: 3000 },
      );
      return;
    }
    // Find the section the user is refining so we can send the current
    // as_json the manager is looking at — including any unsaved local
    // edits — rather than letting the backend pull a stale version from
    // generated_payload_json.
    let target = null;
    (outline?.modules || []).forEach((m) =>
      (m.sections || []).forEach((s) => {
        if (s.uuid === uuid) target = s;
      }),
    );
    if (!target) {
      toast.error("Section not found", { autoClose: 3000 });
      return;
    }
    const currentAsJson = Array.isArray(target.as_json) ? target.as_json : [];

    setRefiningSectionUuid(null);
    // Don't flip section.status to "generating" optimistically — that
    // would collapse the section's expanded panel (the panel gates on
    // status === "ready") and yank the user's view of the content
    // mid-refine. The dedicated submittingSectionForUuid indicator
    // surfaces progress inline with the "Section content (Blocks)"
    // title while keeping the panel open. Mirrors the swipe-deck +
    // quiz refine pattern.
    setSubmittingSectionForUuid(uuid);

    try {
      const res = await postRightworkAPI(
        API_post_learning_generation_section_refine,
        {
          company_id: props.homeCompany,
          job_uuid: jobUuid,
          section_uuid: uuid,
          as_json: currentAsJson,
          edit_prompt: trimmed,
        },
      );
      // Backend returns the full updated job — let the merge helper
      // diff it into local state so as_json / status / error all
      // refresh atomically (same pattern as handleRegenerateSection).
      const job = res?.data?.redux?.payload?.job;
      if (job?.generated_payload_json) {
        mergeGeneratedPayload(job.generated_payload_json);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        "Section refinement failed";
      toast.error(msg, { autoClose: 4000 });
      // Surface the failure on the section card so the manager can see
      // and retry. mergeGeneratedPayload would also fire on a partial
      // 5xx where the backend wrote status=failed before returning, but
      // a network/4xx error skips the merge so we flip locally here.
      updateSectionStatus(uuid, "error");
    } finally {
      setSubmittingSectionForUuid((current) =>
        current === uuid ? null : current,
      );
    }
  };

  // Sub-asset prompt refine. Shared shape for swipe_deck + quiz — the
  // two only differ in which API + spec field they touch, so a single
  // helper keeps the call path DRY.
  const _refineSectionSubAsset = async ({
    uuid, asset, instructions, apiUrl,
    setRefiningUuid, setSubmittingForUuid,
  }) => {
    const trimmed = (instructions || "").trim();
    if (!trimmed) return;
    if (moduleRefineInFlightGuard()) return;
    if (!jobUuid) {
      toast.error("Generation job lost — please regenerate the outline", {
        autoClose: 3000,
      });
      return;
    }
    if (stage === STAGE_FILLING) {
      toast.error(
        "Wait for the other sections to finish before refining.",
        { autoClose: 3000 },
      );
      return;
    }
    let target = null;
    (outline?.modules || []).forEach((m) =>
      (m.sections || []).forEach((s) => {
        if (s.uuid === uuid) target = s;
      }),
    );
    if (!target) {
      toast.error("Section not found", { autoClose: 3000 });
      return;
    }
    const currentSpec = target[asset];
    if (!currentSpec || typeof currentSpec !== "object") {
      toast.error(`No ${asset.replace("_", " ")} to refine`, { autoClose: 3000 });
      return;
    }
    setRefiningUuid(null);
    setSubmittingForUuid(uuid);
    // No optimistic mutation: the asset's local state isn't touched
    // until the success merge fires. So a failed call simply leaves
    // the section showing exactly the spec the manager was viewing
    // before they hit Apply (which is also what the server still
    // has) — the toast is the only signal of failure. We use a
    // longer autoClose than other toasts so the manager has time to
    // notice the failure during a 5-10s refine wait.
    try {
      const res = await postRightworkAPI(apiUrl, {
        company_id: props.homeCompany,
        job_uuid: jobUuid,
        section_uuid: uuid,
        edit_prompt: trimmed,
        [asset]: currentSpec,
      });
      const payload = res?.data?.redux?.payload;
      const job = payload?.job;
      if (job?.generated_payload_json) {
        mergeGeneratedPayload(job.generated_payload_json);
      }
      // Backend slices LLM output to a safety cap (10 quiz questions /
      // 15 deck cards). If the LLM tried to exceed it, surface a toast
      // pointing the manager at the direct-edit escape hatch — the AI
      // refine flow won't grow the asset further, but they can after
      // saving the LMS.
      if (payload?.capped) {
        const noun = asset === "swipe_deck" ? "cards" : "questions";
        const assetLabel = asset === "swipe_deck" ? "Swipe deck" : "Quiz";
        const limit = payload.cap_limit;
        toast.info(
          `${assetLabel} capped at ${limit} ${noun}. To add more, save the LMS and edit the ${asset === "swipe_deck" ? "deck" : "quiz"} directly.`,
          { autoClose: 7000 },
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        "Refinement failed";
      toast.error(msg, { autoClose: 8000 });
    } finally {
      setSubmittingForUuid((current) => (current === uuid ? null : current));
    }
  };

  const handleStartRefineSwipeDeck = (uuid) => setRefiningSwipeDeckUuid(uuid);
  const handleCancelRefineSwipeDeck = () => setRefiningSwipeDeckUuid(null);
  const handleSubmitRefineSwipeDeck = (uuid, instructions) =>
    _refineSectionSubAsset({
      uuid,
      asset: "swipe_deck",
      instructions,
      apiUrl: API_post_learning_generation_swipe_deck_refine,
      setRefiningUuid: setRefiningSwipeDeckUuid,
      setSubmittingForUuid: setSubmittingSwipeDeckForUuid,
    });

  const handleStartRefineQuiz = (uuid) => setRefiningQuizUuid(uuid);
  const handleCancelRefineQuiz = () => setRefiningQuizUuid(null);
  const handleSubmitRefineQuiz = (uuid, instructions) =>
    _refineSectionSubAsset({
      uuid,
      asset: "quiz",
      instructions,
      apiUrl: API_post_learning_generation_quiz_refine,
      setRefiningUuid: setRefiningQuizUuid,
      setSubmittingForUuid: setSubmittingQuizForUuid,
    });

  const handleRevertSectionAsset = async (uuid, asset) => {
    if (moduleRefineInFlightGuard()) return;
    if (!jobUuid) {
      toast.error("Generation job lost — please regenerate the outline", {
        autoClose: 3000,
      });
      return;
    }
    if (stage === STAGE_FILLING) {
      toast.error(
        "Wait for the other sections to finish before reverting.",
        { autoClose: 3000 },
      );
      return;
    }
    // No optimistic update: the snapshot lives server-side, so we let
    // the response payload drive the swap atomically via the merge
    // helper (same pattern as refine submit).
    try {
      const res = await postRightworkAPI(
        API_post_learning_generation_section_revert_asset,
        {
          company_id: props.homeCompany,
          job_uuid: jobUuid,
          section_uuid: uuid,
          asset: asset,
        },
      );
      const job = res?.data?.redux?.payload?.job;
      if (job?.generated_payload_json) {
        mergeGeneratedPayload(job.generated_payload_json);
      }
      toast.success("Reverted to the previous version.", { autoClose: 2500 });
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        "Couldn't revert — please try again.";
      toast.error(msg, { autoClose: 4000 });
    }
  };

  const handleRemoveSectionAsset = async (uuid, asset) => {
    if (asset !== "swipe_deck" && asset !== "quiz") return;
    if (moduleRefineInFlightGuard()) return;
    if (!jobUuid) {
      toast.error("Generation job lost — please regenerate the outline", {
        autoClose: 3000,
      });
      return;
    }
    if (stage === STAGE_FILLING) {
      // Same race protection as Refine / Retry — the backend would 409
      // since the bulk filler can still write the same section's payload.
      toast.error(
        "Wait for the other sections to finish before removing.",
        { autoClose: 3000 },
      );
      return;
    }
    // Snapshot the section's current asset state so a failed API call
    // can roll the optimistic mutation back. Without this, a network
    // error / 409 / 5xx leaves the UI hiding an asset the backend
    // still has — and since the merge helper won't see this section
    // in any future payload that doesn't return it, the drift
    // persists through commit and the manager unknowingly commits an
    // asset they thought was removed.
    let snapshotAsset = null;
    let snapshotSuggest = null;
    (outline?.modules || []).forEach((m) =>
      (m.sections || []).forEach((s) => {
        if (s.uuid === uuid) {
          snapshotAsset = s[asset] ?? null;
          snapshotSuggest = s[`suggest_${asset}`] ?? false;
        }
      }),
    );

    // Optimistic local clear so the pill disappears immediately.
    setOutline((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) =>
        (m.sections || []).forEach((s) => {
          if (s.uuid === uuid) {
            s[asset] = null;
            s[`suggest_${asset}`] = false;
          }
        }),
      );
      return next;
    });
    try {
      const res = await postRightworkAPI(
        API_post_learning_generation_section_remove_asset,
        {
          company_id: props.homeCompany,
          job_uuid: jobUuid,
          section_uuid: uuid,
          asset,
        },
      );
      const job = res?.data?.redux?.payload?.job;
      if (job?.generated_payload_json) {
        mergeGeneratedPayload(job.generated_payload_json);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        "Failed to remove";
      toast.error(msg, { autoClose: 4000 });
      // Roll back to the pre-click state so the UI matches the server.
      setOutline((prev) => {
        if (!prev) return prev;
        const next = JSON.parse(JSON.stringify(prev));
        next.modules.forEach((m) =>
          (m.sections || []).forEach((s) => {
            if (s.uuid === uuid) {
              s[asset] = snapshotAsset;
              s[`suggest_${asset}`] = snapshotSuggest;
            }
          }),
        );
        return next;
      });
    }
  };

  // Open the SectionInputsModal2 pre-populated with the generated
  // content, then save via studioPostLearningSection (below) which
  // routes to the payload-update endpoint instead of the real
  // post_learning_section. Edits live in generated_payload_json
  // until the user hits Commit; if they bail, edits are lost (no
  // commit = no rows). Quiz/swipe-deck inline embeds inside the
  // editor are stubbed out via no-op props — AI-generated content
  // doesn't contain those blocks anyway, and managers can add them
  // post-commit via the regular section editor.
  const handleEditSection = (uuid) => {
    if (moduleRefineInFlightGuard()) return;
    // Defense-in-depth alongside the edit button's disabled gate
    // (mirrors handleApproveOutline's isOutlining guard): the save
    // endpoint rejects FILLING jobs with a 409, so never open an
    // editor whose save can't succeed. Note a FAILED job also parks
    // the studio at STAGE_FILLING (with "Resume polling"), so manual
    // edits stay gated there too — errored sections recover via the
    // per-section Retry instead.
    if (stage === STAGE_FILLING) return;
    let found = null;
    let parentModuleUuid = null;
    (outline?.modules || []).forEach((m) =>
      (m.sections || []).forEach((s) => {
        if (s.uuid === uuid) {
          found = s;
          parentModuleUuid = m.uuid;
        }
      }),
    );
    if (!found) {
      toast.error("Section not found", { autoClose: 3000 });
      return;
    }
    // BlockNote's default media schemas don't include `file_uuid`, so
    // loading as_json into the editor strips the prop and the document
    // comes back holding only ~1-hour signed URLs. Record url->uuid
    // here so studioPostLearningSection can swap the durable uuid back
    // in at save — the same localStorage mechanism the post-commit
    // editor relies on (CreateTrainingContentDisplay's
    // post_learning_section + SectionInputsModal2's uploadFile).
    const asJson = Array.isArray(found.as_json) ? found.as_json : [];
    try {
      const map = JSON.parse(
        localStorage.getItem("mapSignedUrlToFileUuid") || "{}",
      );
      let changed = false;
      asJson.forEach((block) => {
        const validTypes = ["image", "video", "audio", "file", "pdf"];
        if (
          validTypes.includes(block?.type)
          && block?.props?.url
          && block?.props?.file_uuid
        ) {
          map[block.props.url] = block.props.file_uuid;
          changed = true;
        }
      });
      if (changed) {
        localStorage.setItem("mapSignedUrlToFileUuid", JSON.stringify(map));
      }
    } catch (e) {
      // Corrupt map entry — media will save url-only and re-sign fails
      // post-expiry, but don't block opening the editor over it.
      console.error("Failed to record media uuid map", e);
    }
    setEditSectionData({
      uuid: found.uuid,
      name: found.name || "",
      as_json: asJson,
      category: "",
      // Full-LMS-only: the tracks that contain the section's parent
      // module. Empty list for non-full_lms or non-shared. The modal
      // uses this to render the "Changes here affect N tracks" banner
      // so the manager sees the consequence at the moment of edit.
      sharedAcrossTracks: isFullLmsMode
        ? (tracksByModuleUuid.get(parentModuleUuid) || [])
        : [],
    });
    setEditSectionOpen(true);
  };

  // Custom save fn passed to SectionInputsModal2 in place of
  // post_learning_section. Persists name + as_json into
  // generated_payload_json via the new payload-update endpoint, then
  // merges the response back into outline state. Returns the section
  // shape SectionInputsModal2's onSaveSuccess callback expects (just
  // a uuid is enough — we're the create path's caller and know the
  // uuid already).
  const studioPostLearningSection = async () => {
    if (!jobUuid || !editSectionData) return null;
    // Re-attach file_uuid to media blocks by their signed URL (recorded
    // at edit-open and by in-editor uploads) — mirrors the swap in
    // CreateTrainingContentDisplay's post_learning_section.
    let urlToUuid = {};
    try {
      urlToUuid = JSON.parse(
        localStorage.getItem("mapSignedUrlToFileUuid") || "{}",
      );
    } catch (e) {
      urlToUuid = {};
    }
    const validTypes = ["image", "video", "audio", "file", "pdf"];
    const editedJson = (editSectionData.as_json || []).map((element) => {
      if (!validTypes.includes(element?.type)) return element;
      if (element?.props && "file_uuid" in element.props) return element;
      const uuidVal = urlToUuid[element?.props?.url];
      if (uuidVal) {
        return {
          ...element,
          props: { ...element.props, file_uuid: uuidVal },
        };
      }
      return element;
    });
    try {
      const res = await postRightworkAPI(
        API_post_learning_generation_section_payload_update,
        {
          company_id: props.homeCompany,
          job_uuid: jobUuid,
          section_uuid: editSectionData.uuid,
          name: editSectionData.name,
          as_json: editedJson,
        },
      );
      const job = res?.data?.redux?.payload?.job;
      if (job?.generated_payload_json) {
        mergeGeneratedPayload(job.generated_payload_json);
      }
      toast.success("Section updated", { autoClose: 2000 });
      return { uuid: editSectionData.uuid };
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        "Failed to save section";
      toast.error(msg, { autoClose: 4000 });
      return null;
    }
  };

  const handleRemoveSection = (uuid) => {
    // For full-LMS outlines, mirror the `handleRemoveModule` pattern:
    // if removing this section empties its parent module, treat that
    // as also removing the module (so tracks that referenced it can
    // be reconciled in the same update). Without this, an empty
    // module would survive in `outline.modules` with its uuid in
    // validModuleUuids, but the backend approval normalizer drops
    // empty modules (no sections) and then drops any track that only
    // referenced them — a silent "track disappeared on approve" path.
    //
    // Find the parent module + figure out whether this removal empties
    // it. Computed from the closure-captured outline so the toast
    // fires exactly once (mirrors handleRemoveModule's strict-mode
    // double-run protection).
    let droppedModuleUuid = null;
    const droppedTrackNames = [];
    if (outline && outline.target_kind === "full_lms") {
      (outline.modules || []).forEach((m) => {
        const remainingSections = (m.sections || []).filter(
          (s) => s.uuid !== uuid,
        );
        if (
          remainingSections.length === 0
          && (m.sections || []).some((s) => s.uuid === uuid)
        ) {
          droppedModuleUuid = m.uuid;
        }
      });
      if (droppedModuleUuid && Array.isArray(outline.tracks)) {
        outline.tracks.forEach((t) => {
          const refs = t.module_uuids || [];
          if (!refs.includes(droppedModuleUuid)) return;
          const remaining = refs.filter((u) => u !== droppedModuleUuid);
          if (remaining.length === 0) {
            droppedTrackNames.push(t.name || "Unnamed track");
          }
        });
      }
    }

    setOutline((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) => {
        m.sections = m.sections.filter((s) => s.uuid !== uuid);
      });
      // Full-LMS: if a module just lost its last section, drop the
      // module too and reconcile track refs. Skip in track/module
      // modes — there's no shared registry, and an empty module is
      // still committable as long as the user re-adds sections.
      if (prev.target_kind === "full_lms" && droppedModuleUuid) {
        next.modules = next.modules.filter(
          (m) => m.uuid !== droppedModuleUuid,
        );
        if (Array.isArray(next.tracks)) {
          next.tracks = next.tracks
            .map((t) => ({
              ...t,
              module_uuids: (t.module_uuids || []).filter(
                (u) => u !== droppedModuleUuid,
              ),
            }))
            .filter((t) => (t.module_uuids || []).length > 0);
        }
      }
      return next;
    });

    if (droppedModuleUuid) {
      const moduleName =
        (outline.modules || []).find((m) => m.uuid === droppedModuleUuid)?.name
        || "Unnamed module";
      const trackSuffix = droppedTrackNames.length > 0
        ? ` and ${droppedTrackNames.length} track${
            droppedTrackNames.length > 1 ? "s" : ""
          } that only used it: ${droppedTrackNames.join(", ")}`
        : "";
      toast.info(
        `Module "${moduleName}" had no remaining sections — also removed it${trackSuffix}.`,
        { autoClose: 5000 },
      );
    }
  };

  const handleToggleQuiz = (uuid) => {
    setOutline((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) =>
        m.sections.forEach((s) => {
          if (s.uuid === uuid) s.suggest_quiz = !s.suggest_quiz;
        })
      );
      return next;
    });
  };

  const handleToggleSwipeDeck = (uuid) => {
    setOutline((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) =>
        m.sections.forEach((s) => {
          if (s.uuid === uuid) s.suggest_swipe_deck = !s.suggest_swipe_deck;
        })
      );
      return next;
    });
  };

  const handleRenameModule = (moduleUuid, name) => {
    setOutline((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) => {
        if (m.uuid === moduleUuid) m.name = name;
      });
      if (next.target_kind === "module") next.name = name;
      return next;
    });
    if (outline?.target_kind === "module") setOutlineName(name);
  };

  const handleRenameSection = (sectionUuid, name) => {
    setOutline((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) =>
        m.sections.forEach((s) => {
          if (s.uuid === sectionUuid) s.name = name;
        }),
      );
      return next;
    });
  };

  const handleEditSectionBrief = (sectionUuid, brief) => {
    setOutline((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) =>
        m.sections.forEach((s) => {
          if (s.uuid === sectionUuid) s.brief = brief;
        }),
      );
      return next;
    });
  };

  const handleRemoveModule = (moduleUuid) => {
    // For full-LMS outlines, scrub references to this module from every
    // track's module_uuids in the same update. Without this, tracks
    // referencing the removed module would carry stale uuids → the
    // backend approval normalizer would silently drop those uuids
    // (normalize_approved_full_lms_outline), and tracks that lose ALL
    // their references would themselves be dropped — a "valid-looking
    // outline disappearing at approval time" failure mode.
    //
    // If a track ends up with zero module references, drop it here too
    // and toast the manager. Mirrors the backend's track-with-empty-
    // module_uuids policy, but surfaced at the moment of the user
    // action rather than at approval.
    //
    // droppedTrackNames is computed from the closure-captured outline
    // (safe for click-handler closures) so the toast can fire exactly
    // once, after the setState is queued. Computing inside the
    // updater callback would double-toast in React strict-mode where
    // updaters run twice.
    const droppedTrackNames = [];
    if (outline && outline.target_kind === "full_lms" && Array.isArray(outline.tracks)) {
      outline.tracks.forEach((t) => {
        const refs = t.module_uuids || [];
        if (!refs.includes(moduleUuid)) return;
        const remaining = refs.filter((u) => u !== moduleUuid);
        if (remaining.length === 0) {
          droppedTrackNames.push(t.name || "Unnamed track");
        }
      });
    }

    setOutline((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.modules = next.modules.filter((m) => m.uuid !== moduleUuid);
      if (prev.target_kind === "full_lms" && Array.isArray(next.tracks)) {
        next.tracks = next.tracks
          .map((t) => ({
            ...t,
            module_uuids: (t.module_uuids || []).filter(
              (u) => u !== moduleUuid,
            ),
          }))
          .filter((t) => (t.module_uuids || []).length > 0);
      }
      return next;
    });

    if (droppedTrackNames.length > 0) {
      toast.info(
        `Also removed ${droppedTrackNames.length} track${
          droppedTrackNames.length > 1 ? "s" : ""
        } with no remaining modules: ${droppedTrackNames.join(", ")}`,
        { autoClose: 5000 },
      );
    }
  };

  const handleAddSection = (moduleUuid) => {
    setOutline((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.forEach((m) => {
        if (m.uuid === moduleUuid) {
          m.sections.push({
            uuid: `s-new-${Date.now()}`,
            name: "New section",
            brief: "Describe what this covers",
            suggest_swipe_deck: false,
            suggest_quiz: false,
            status: "pending",
          });
        }
      });
      return next;
    });
  };

  const handleAddModule = () => {
    setOutline((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.modules.push({
        uuid: `m-new-${Date.now()}`,
        name: "New module",
        sections: [],
      });
      return next;
    });
  };

  // Reorder modules + cross-module section moves. droppableId for the
  // sections list encodes the parent module's uuid as `module-{uuid}`
  // so a drop event can locate both source and destination modules
  // without relying on render order.
  const handleDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (type === "module") {
      setOutline((prev) => {
        if (!prev) return prev;
        const modules = [...prev.modules];
        const [moved] = modules.splice(source.index, 1);
        modules.splice(destination.index, 0, moved);
        return { ...prev, modules };
      });
      return;
    }

    if (type === "section") {
      setOutline((prev) => {
        if (!prev) return prev;
        const sourceModuleUuid = source.droppableId.replace(/^module-/, "");
        const destModuleUuid = destination.droppableId.replace(/^module-/, "");
        // Clone modules + each module's sections array so we can mutate
        // the two affected lists without leaking shared references.
        const modules = prev.modules.map((m) => ({
          ...m,
          sections: [...m.sections],
        }));
        const sourceModule = modules.find((m) => m.uuid === sourceModuleUuid);
        const destModule = modules.find((m) => m.uuid === destModuleUuid);
        if (!sourceModule || !destModule) return prev;
        const [moved] = sourceModule.sections.splice(source.index, 1);
        if (!moved) return prev;
        destModule.sections.splice(destination.index, 0, moved);
        return { ...prev, modules };
      });
    }
  };

  const handleStartRefineModule = (uuid) => {
    setRefiningModuleUuid((prev) => (prev === uuid ? null : uuid));
  };

  // Module-level refine = the section refine applied to every finished
  // section in the module, SEQUENTIALLY, via the existing per-section
  // endpoint. Deliberately not a new backend endpoint or task:
  //   - each call re-fetches and writes the whole generated_payload_json,
  //     so running sections concurrently would lose writes — one at a
  //     time is the safe cadence, and the per-section spinner doubles as
  //     a progress indicator as the loop walks the module;
  //   - reusing section_refine means every section gets its own
  //     as_json_previous snapshot, so the manager can undo any single
  //     section of a module-wide pass they don't like;
  //   - a synchronous loop endpoint would outlive gateway timeouts, and
  //     a celery task isn't locally testable (workers run deployed code).
  const handleSubmitRefineModule = async (uuid, instructions) => {
    const trimmed = (instructions || "").trim();
    if (!trimmed) return;
    if (!jobUuid) {
      toast.error("Generation job lost — please regenerate the outline", {
        autoClose: 3000,
      });
      return;
    }
    // Defense-in-depth alongside the button gates (mirrors the section
    // handler): refining mid-fill would race the background filler.
    if (stage !== STAGE_READY) {
      toast.error(
        "Wait for all sections to finish before refining.",
        { autoClose: 3000 },
      );
      return;
    }
    if (submittingModuleUuid) return;
    // Symmetric to moduleRefineInFlightGuard: a single-section refine
    // (content, deck, or quiz) already in flight does the same whole-
    // payload read-modify-write the loop does — let it land first.
    if (
      submittingSectionForUuid
      || submittingSwipeDeckForUuid
      || submittingQuizForUuid
    ) {
      toast.error("Wait for the current refine to finish.", {
        autoClose: 3000,
      });
      return;
    }
    const mod = (outline?.modules || []).find((m) => m.uuid === uuid);
    if (!mod) {
      toast.error("Module not found", { autoClose: 3000 });
      return;
    }
    // Only sections with finished content. Failed/pending sections have
    // nothing to rewrite — they recover via the per-section Retry. The
    // captured as_json per section is the manager's current view; other
    // iterations' merges never touch it, so reading from this snapshot
    // stays correct across the loop.
    const targets = (mod.sections || []).filter(
      (s) =>
        s.status === "ready"
        && Array.isArray(s.as_json)
        && s.as_json.length > 0,
    );
    if (targets.length === 0) {
      toast.error("No finished sections to refine in this module", {
        autoClose: 3000,
      });
      return;
    }
    setRefiningModuleUuid(null);
    setSubmittingModuleUuid(uuid);
    let failedCount = 0;
    try {
      for (const [i, s] of targets.entries()) {
        // Studio unmounted (cancelPollRef flips in the unmount effect) —
        // stop issuing requests; completed sections are already saved.
        if (cancelPollRef.current) return;
        setModuleRefineProgress({ done: i, total: targets.length });
        setSubmittingSectionForUuid(s.uuid);
        try {
          const res = await postRightworkAPI(
            API_post_learning_generation_section_refine,
            {
              company_id: props.homeCompany,
              job_uuid: jobUuid,
              section_uuid: s.uuid,
              as_json: s.as_json,
              edit_prompt: trimmed,
            },
          );
          const job = res?.data?.redux?.payload?.job;
          if (job?.generated_payload_json) {
            mergeGeneratedPayload(job.generated_payload_json);
          }
        } catch (err) {
          // A 409 means the JOB shifted under us (committed or reset in
          // another tab) — every remaining call would fail identically,
          // so stop instead of painting the rest of the module red.
          // The attempted section keeps its server-side state; nothing
          // was overwritten.
          if (err?.response?.status === 409) {
            toast.error(
              err?.response?.data?.system?.message
                || "Job state changed — refresh and try again.",
              { autoClose: 4000 },
            );
            return;
          }
          // Otherwise keep walking: one section's failure shouldn't
          // waste the refines that already landed or block the rest.
          failedCount += 1;
          updateSectionStatus(s.uuid, "error");
        } finally {
          setSubmittingSectionForUuid((current) =>
            current === s.uuid ? null : current,
          );
        }
      }
      if (failedCount === 0) {
        toast.success(
          `Module refined — ${targets.length} section${
            targets.length === 1 ? "" : "s"
          } updated`,
          { autoClose: 3000 },
        );
      } else {
        toast.error(
          `Refined ${targets.length - failedCount} of ${targets.length} sections — use Retry on the failed one${
            failedCount === 1 ? "" : "s"
          } to regenerate.`,
          { autoClose: 5000 },
        );
      }
    } finally {
      setModuleRefineProgress(null);
      setSubmittingModuleUuid((current) => (current === uuid ? null : current));
    }
  };

  const handleOutlinePromptSubmit = async (instructions) => {
    const trimmed = (instructions || "").trim();
    if (!trimmed) return;
    if (!jobUuid || !outline) {
      toast.error("Outline isn't ready yet", { autoClose: 3000 });
      return;
    }
    // The button + textarea are already disabled while busy, but guard
    // again in case a second submit slips through (e.g. via Enter).
    if (isOutlining) return;

    // Live-streaming UX: clear the outline's modules and rebuild them as
    // events arrive — same pattern as the initial-generation flow so an
    // edit feels as responsive as a fresh generation. The pre-edit state
    // is snapshotted first so an aborted or failed stream rolls back
    // cleanly instead of leaving a half-rebuilt outline on screen.
    const preEditOutline = JSON.parse(JSON.stringify(outline));
    const preEditOutlineName = outlineName;

    if (abortStreamRef.current) {
      try { abortStreamRef.current.abort(); } catch (_) { /* noop */ }
    }
    const controller = new AbortController();
    abortStreamRef.current = controller;
    setIsOutlining(true);
    setIsEditingOutline(true);
    // Clear both modules and (for full_lms) tracks so the rebuild window
    // doesn't render stale tracks alongside the freshly-emptied modules.
    // For non-full_lms outlines, `tracks` is absent and the spread leaves
    // it untouched.
    setOutline((prev) => {
      if (!prev) return prev;
      const next = { ...prev, modules: [] };
      if (prev.target_kind === "full_lms") next.tracks = [];
      return next;
    });

    try {
      await streamOutlineEditAPI(
        API_post_learning_generation_outline_edit_stream,
        {
          company_id: props.homeCompany,
          job_uuid: jobUuid,
          edit_prompt: trimmed,
          // Send the outline the manager is actually looking at, not the
          // last server-persisted version. Rename / reorder / add /
          // remove actions mutate local state only until approval, and
          // without this the LLM would edit a stale base and the `done`
          // swap would silently overwrite the manager's unsaved edits.
          outline_json: stripOutlineForApproval(preEditOutline, roleNameById),
        },
        {
          onProgram: (name) => {
            setOutline((prev) => (prev ? { ...prev, name } : prev));
            setOutlineName(name);
          },
          onModule: (name, moduleIndex) => {
            if (
              !Number.isInteger(moduleIndex) ||
              moduleIndex < 0 ||
              moduleIndex > MAX_STREAMED_MODULE_INDEX
            ) {
              return;
            }
            setOutline((prev) => {
              if (!prev) return prev;
              const modules = [...prev.modules];
              while (modules.length <= moduleIndex) {
                modules.push({
                  uuid: tempUuid(),
                  name: "",
                  sections: [],
                });
              }
              modules[moduleIndex] = { ...modules[moduleIndex], name };
              return { ...prev, modules };
            });
          },
          onSection: (section, moduleIndex) => {
            if (
              !Number.isInteger(moduleIndex) ||
              moduleIndex < 0 ||
              moduleIndex > MAX_STREAMED_MODULE_INDEX
            ) {
              return;
            }
            setOutline((prev) => {
              if (!prev) return prev;
              const modules = [...prev.modules];
              while (modules.length <= moduleIndex) {
                modules.push({
                  uuid: tempUuid(),
                  name: "",
                  sections: [],
                });
              }
              const target = modules[moduleIndex];
              if (
                (target.sections?.length ?? 0)
                  >= MAX_STREAMED_SECTIONS_PER_MODULE
              ) {
                return prev;
              }
              modules[moduleIndex] = {
                ...target,
                sections: [
                  ...target.sections,
                  { ...section, uuid: tempUuid(), status: "pending" },
                ],
              };
              return { ...prev, modules };
            });
          },
          onTrack: (track, trackIndex) => {
            // Full-LMS edit mode emits track events with
            // `module_handles` (strings) instead of `module_indices`
            // (ints). The live tracks panel can show name + roles
            // immediately; module references render as "Resolving…"
            // until the canonical `done` arrives with real uuids.
            // Single-track / module edit streams never emit track
            // events so this is a no-op there.
            if (
              !Number.isInteger(trackIndex) ||
              trackIndex < 0 ||
              trackIndex > MAX_STREAMED_TRACK_INDEX
            ) {
              return;
            }
            setOutline((prev) => {
              if (!prev) return prev;
              const tracks = [...(prev.tracks || [])];
              while (tracks.length <= trackIndex) {
                tracks.push({
                  uuid: tempUuid(),
                  name: "",
                  role_names: [],
                  module_indices: [],
                  module_handles: [],
                  module_uuids: [],
                  role_ids: [],
                });
              }
              tracks[trackIndex] = {
                ...tracks[trackIndex],
                name: track.name,
                role_names: track.role_names || [],
                // Edit mode primarily ships module_handles; fresh-mode
                // (shouldn't happen on the edit endpoint but defensive)
                // ships module_indices. Carry both forward verbatim.
                module_handles: track.module_handles || [],
                module_indices: track.module_indices || [],
              };
              return { ...prev, tracks };
            });
          },
          onDone: (job) => {
            // Swap in the canonical outline (with server-generated
            // UUIDs) so subsequent approval/regen calls send the right
            // identifiers — same merge pattern as initial generation.
            const canonical = job?.outline_json;
            if (!canonical) return;
            const cloned = JSON.parse(JSON.stringify(canonical));
            cloned.modules.forEach((m) =>
              (m.sections || []).forEach((s) => (s.status = "pending"))
            );
            setOutline(cloned);
            setOutlineName(cloned.name);
            setOutlineRevertAvailable(Boolean(job.outline_revert_available));
          },
          onError: () => { /* outer catch handles toasting */ },
        },
        { signal: controller.signal },
      );
    } catch (err) {
      // Restore the pre-edit snapshot on any non-success exit. The
      // backend doesn't persist a new outline on stream failure (its
      // save is gated on a successful normalize), so the snapshot is
      // exactly what the canonical job state still holds.
      setOutline(preEditOutline);
      setOutlineName(preEditOutlineName);
      // Aborts are intentional (component unmount, or a regenerate
      // taking over) — silent rollback, no toast.
      if (err?.name === "AbortError") return;
      const msg =
        err?.message ||
        err?.response?.data?.system?.message ||
        "Failed to edit outline";
      toast.error(msg, { autoClose: 4000 });
    } finally {
      if (abortStreamRef.current === controller) {
        setIsOutlining(false);
        setIsEditingOutline(false);
        abortStreamRef.current = null;
      }
    }
  };

  const handleRevertLastEdit = async () => {
    if (!jobUuid || !outlineRevertAvailable || isOutlining) return;
    try {
      const res = await postRightworkAPI(
        API_post_learning_generation_outline_revert,
        {
          company_id: props.homeCompany,
          job_uuid: jobUuid,
        },
      );
      const job = res?.data?.redux?.payload?.job;
      const canonical = job?.outline_json;
      if (!canonical) {
        toast.error("Couldn't revert outline", { autoClose: 4000 });
        return;
      }
      const cloned = JSON.parse(JSON.stringify(canonical));
      cloned.modules.forEach((m) =>
        (m.sections || []).forEach((s) => (s.status = "pending"))
      );
      setOutline(cloned);
      setOutlineName(cloned.name);
      // Backend cleared outline_json_previous on revert, so this flips
      // false — the manager can no longer undo the undo.
      setOutlineRevertAvailable(Boolean(job.outline_revert_available));
      toast.success("Edit reverted", { autoClose: 2000 });
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        "Failed to revert outline";
      toast.error(msg, { autoClose: 4000 });
    }
  };

  const handleToggleExpandSection = (uuid) => {
    setExpandedSections((prev) =>
      prev.includes(uuid) ? prev.filter((x) => x !== uuid) : [...prev, uuid]
    );
    if (refiningSectionUuid === uuid) setRefiningSectionUuid(null);
  };

  const handleCancelRefineSection = () => setRefiningSectionUuid(null);
  const handleCancelRefineModule = () => setRefiningModuleUuid(null);

  const handleEditPrompt = () => {
    if (moduleRefineInFlightGuard()) return;
    // If a stream is in flight, abort it so the backend stops generating
    // (and billing) tokens for an outline the user is walking away from.
    // The backend's GeneratorExit handler marks the job FAILED, so no
    // orphaned awaiting_approval row is left behind — except in the
    // narrow window where outline_json was just persisted before the
    // abort lands, in which case a stale (but cheap) row remains.
    //
    // Nulling the ref before the abort ensures the in-flight call's
    // finally block sees `abortStreamRef.current !== controller` and
    // skips its own state reset, so we own the cleanup here.
    if (abortStreamRef.current) {
      const controller = abortStreamRef.current;
      abortStreamRef.current = null;
      try { controller.abort(); } catch (_) { /* noop */ }
    }
    setIsOutlining(false);
    setOutline(null);
    setOutlineName("");
    setJobUuid(null);
    setExpandedSections([]);
    setRefiningSectionUuid(null);
    setRefiningModuleUuid(null);
    setStage(STAGE_PROMPT);
  };

  const [isCommitting, setIsCommitting] = React.useState(false);

  const handleSaveAsTrack = async () => {
    if (isCommitting || !jobUuid) return;
    if (moduleRefineInFlightGuard()) return;
    setIsCommitting(true);
    // Label drives the success toast wording. Full-LMS commits create
    // multiple tracks at once — surface that explicitly so the manager
    // knows how much got materialized.
    const tk = outline?.target_kind;
    const label = tk === "track" ? "Track" : tk === "module" ? "Module" : "LMS";
    try {
      const res = await postRightworkAPI(API_post_learning_generation_commit, {
        company_id: props.homeCompany,
        job_uuid: jobUuid,
      });
      const skipped = res.data?.redux?.payload?.skipped_section_count || 0;
      const committedJob = res.data?.redux?.payload?.job;
      const trackCount = committedJob?.committed_track_uuids?.length || 0;

      let message;
      if (tk === "full_lms") {
        message =
          trackCount > 0
            ? `LMS saved — ${trackCount} track${trackCount === 1 ? "" : "s"} created`
            : "LMS saved!";
        if (skipped > 0) {
          message += ` (${skipped} failed section${skipped > 1 ? "s" : ""} skipped)`;
        }
      } else {
        message =
          skipped > 0
            ? `${label} saved — skipped ${skipped} failed section${skipped > 1 ? "s" : ""}`
            : `${label} saved!`;
      }
      toast.success(message, { autoClose: 3500 });
      if (props.onSaved) props.onSaved();
      if (props.onClose) props.onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.system?.message ||
        err?.message ||
        `Failed to save ${label.toLowerCase()}`;
      toast.error(msg, { autoClose: 4000 });
    } finally {
      setIsCommitting(false);
    }
  };

  const canSave = stage === STAGE_READY && !isCommitting;
  const saveLabel =
    targetKind === "track"
      ? "Save as Track"
      : targetKind === "module"
      ? "Save as Module"
      : "Save LMS";
  const swipeDeckCount = allSections.filter((s) => s.suggest_swipe_deck).length;
  const quizCount = allSections.filter((s) => s.suggest_quiz).length;

  return (
    <div className="w-full min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 z-30">
        <div className="px-6 py-4 flex items-center gap-4">
          <button
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
            onClick={props.onClose}
          >
            <ArrowBackIcon />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <AutoAwesomeIcon className="text-violet-700" style={{ fontSize: 18 }} />
            </div>
            <div>
              <div className="text-base font-semibold text-slate-900">
                AI Training Studio
              </div>
              <div className="text-xs text-slate-500">
                Generate complete training content from a prompt + reference docs
              </div>
            </div>
          </div>
          <div className="flex-1 flex justify-center">
            <StageStepper stage={stage} />
          </div>
          <button
            // Fixed min width + centered content so the label switching between
            // "Save as Track" / "Save as Module" / "Save LMS" (and "Saving…")
            // doesn't change the button width and shift the centered stepper.
            className={`px-5 py-2 rounded-lg font-medium text-sm transition-colors inline-flex items-center justify-center gap-2 min-w-[150px] ${
              canSave
                ? "bg-violet-700 hover:bg-violet-800 text-white"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
            disabled={!canSave}
            onClick={handleSaveAsTrack}
          >
            {isCommitting && (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            {isCommitting ? "Saving…" : saveLabel}
          </button>
        </div>
      </div>

      {/* Body — fixed `height` (not minHeight) so the right-pane's
          empty-canvas centering is anchored to the viewport regardless
          of how tall the left pane grows. Without this, a taller left
          pane (e.g. full-LMS picker + roles list + prompt + references)
          would push the flex row past the viewport, and EmptyCanvas's
          h-full + flex items-center would then center against the new
          taller height — visually below the fold. Both panes have
          `overflow-y-auto` so they scroll internally instead of pushing
          the row. */}
      <div className="flex" style={{ height: "calc(100vh - 73px)" }}>
        {/* Left pane — control panel */}
        <div className="w-[380px] border-r border-slate-200 bg-white p-6 space-y-6 overflow-y-auto">
          {stage === STAGE_PROMPT ? (
            <>
              <TargetKindPicker
                value={targetKind}
                onChange={setTargetKind}
                disabled={isOutlining}
              />
              {targetKind === "full_lms" && (
                <LmsRolesPicker
                  roles={props.roles || []}
                  selectedRoleIds={selectedRoleIds}
                  onChange={setSelectedRoleIds}
                  disabled={isOutlining}
                />
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {targetKind === "track"
                    ? "What training program do you need?"
                    : targetKind === "module"
                    ? "What module do you need?"
                    : "Anything specific you want the AI to focus on? (optional)"}
                </label>
                <TextField
                  placeholder={
                    targetKind === "track"
                      ? "Example: A 2-week onboarding track for new servers covering values, service standards, menu knowledge, and POS basics."
                      : targetKind === "module"
                      ? "Example: A single module on customer service fundamentals — reading the room, greeting scripts, recovery moments."
                      : "Optional. The AI will design tracks and modules around the roles + reference docs above. Add tone or scope guidance here if you have any."
                  }
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  disabled={isOutlining}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      borderRadius: "8px",
                      fontSize: "14px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    },
                  }}
                />
              </div>
              <ReferencePickerTrigger
                files={availableFiles}
                folders={availableFolders}
                modules={props.modulesData || []}
                sections={props.sectionsData || []}
                isLoading={isLoadingFiles}
                selectedFileUuids={selectedFiles}
                selectedFolderUuids={selectedFolders}
                selectedModuleUuids={selectedLmsModules}
                selectedSectionUuids={selectedLmsSections}
                onOpenPicker={() => setPickerOpen(true)}
                onRemoveFile={handleToggleFile}
                onRemoveFolder={handleToggleFolder}
                onRemoveModule={handleToggleLmsModule}
                onRemoveSection={handleToggleLmsSection}
                disabled={isOutlining}
              />
              <div
                className={`p-3 rounded-lg border border-slate-200 ${
                  isOutlining ? "opacity-60 pointer-events-none" : ""
                }`}
              >
                <span className="block text-sm font-medium text-gray-700 mb-0.5">
                  Images
                </span>
                <span className="block text-xs text-slate-500 mb-3">
                  Covers, swipe deck cards, and section illustrations. Reuse
                  real photos from your reference materials, add AI-generated
                  images, or keep it text-only.
                </span>
                <ImageSourceControl
                  value={imageMode}
                  onChange={setImageMode}
                  label=""
                />
              </div>
              <button
                className="w-full px-6 py-3 bg-violet-700 hover:bg-violet-800 text-white rounded-lg font-medium transition-colors disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                onClick={handleGenerateOutline}
                disabled={
                  isOutlining
                  // Track/module require a prompt; full-LMS requires at
                  // least one role (the planning anchor) but prompt is
                  // optional.
                  || (targetKind !== "full_lms" && !prompt.trim())
                  || (targetKind === "full_lms" && selectedRoleIds.length === 0)
                }
              >
                {isOutlining ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Reading your docs…
                  </>
                ) : (
                  <>
                    <AutoAwesomeIcon style={{ fontSize: 18 }} />
                    Generate outline
                  </>
                )}
              </button>
              <p className="text-xs text-slate-500">
                Outline only — no content is generated until you approve.
              </p>
            </>
          ) : (
            <>
              <ContextSummary
                prompt={prompt}
                fileUuids={selectedFiles}
                folderUuids={selectedFolders}
                files={availableFiles}
                folders={availableFolders}
                moduleUuids={selectedLmsModules}
                sectionUuids={selectedLmsSections}
                modules={props.modulesData || []}
                sections={props.sectionsData || []}
                targetKind={targetKind}
                // Full-LMS only: surface the manager's role picks in
                // the post-prompt summary. Roles are the planning
                // anchor for full_lms (the prompt is optional), so
                // showing them is more useful than the prompt text.
                roleIds={selectedRoleIds}
                roleNameById={roleNameById}
                onEdit={handleEditPrompt}
                // Ungate edit when polling has stalled so the user has
                // an escape hatch to start over even though the backend
                // job is technically in flight.
                disabled={stage === STAGE_FILLING && !pollRetryAvailable}
              />
              {(stage === STAGE_FILLING || stage === STAGE_READY) && !pollRetryAvailable && (
                <ProgressCard
                  sections={allSections}
                  // Sections finish before the job flips to READY while images
                  // are still rendering — show a "Finalizing" state instead of
                  // "Generation complete" so the disabled Save button doesn't
                  // read as broken. Images (swipe-card images + section
                  // illustrations) generate for any mode with images on; covers
                  // additionally generate for track / full_lms (modules have none).
                  finalizing={stage === STAGE_FILLING}
                  imagesExpected={imageMode !== "none"}
                  coversExpected={imageMode !== "none" && !isModuleMode}
                />
              )}
              {stage === STAGE_FILLING && pollRetryAvailable && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
                  <div className="text-sm font-semibold text-amber-900">
                    Polling stopped
                  </div>
                  <p className="text-xs text-amber-800">
                    The backend may still be filling sections. Resume to
                    keep checking, or use Edit prompt and references above
                    to start over.
                  </p>
                  <button
                    onClick={handleResumePolling}
                    className="w-full px-3 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-medium"
                  >
                    Resume polling
                  </button>
                </div>
              )}
              {stage === STAGE_OUTLINE && isOutlining && (
                <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-violet-900">
                    <div className="w-3.5 h-3.5 border-2 border-violet-700 border-t-transparent rounded-full animate-spin" />
                    Generating outline…
                  </div>
                  <p className="text-xs text-violet-800">
                    Modules and sections will appear as they're generated.
                    You'll be able to review and edit when the outline is
                    ready.
                  </p>
                </div>
              )}
              {stage === STAGE_OUTLINE && !isOutlining && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
                  <div className="text-sm font-semibold text-amber-900">
                    Review the outline
                  </div>
                  <p className="text-xs text-amber-800">
                    Rename sections, toggle swipe decks and quizzes per section,
                    or use the prompt bar at the top for structural changes.
                    Approve when ready.
                  </p>
                  <div className="text-xs text-amber-800 pt-2 border-t border-amber-200 space-y-0.5">
                    <div><span className="font-medium">{allSections.length}</span> sections (every section gets blocks)</div>
                    <div><span className="font-medium">{swipeDeckCount}</span> embedded swipe decks</div>
                    <div><span className="font-medium">{quizCount}</span> embedded quizzes</div>
                  </div>
                </div>
              )}
              {stage === STAGE_READY && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                    <CheckCircleIcon style={{ fontSize: 18 }} />
                    Ready to save
                  </div>
                  <p className="text-xs text-emerald-800">
                    All sections generated. Review previews on the right,
                    refine anything with prompts, then save.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right pane — canvas */}
        <div className="flex-1 overflow-y-auto">
          {stage === STAGE_PROMPT ? (
            <EmptyCanvas />
          ) : (
            <div className="max-w-3xl mx-auto px-8 py-8 space-y-6">
              {/* Outline-level prompt edit. Only shown during outline
                  review (STAGE_OUTLINE) — once filling/ready, structural
                  edits would orphan generated content, so hide the bar
                  rather than dance around it. */}
              {stage === STAGE_OUTLINE && !!outline && (
                <OutlinePromptBar
                  onSubmit={handleOutlinePromptSubmit}
                  isModuleMode={isModuleMode}
                  // Disable during any in-flight stream — initial
                  // generation as well as edits — so a manager can't
                  // submit an edit before the underlying job hits
                  // AWAITING_APPROVAL (the edit endpoint would 409).
                  busy={isOutlining}
                  // Show the "Rewriting outline…" status only on real
                  // edits. During initial generation the bar is
                  // disabled but stays silent.
                  editing={isEditingOutline}
                />
              )}

              {!isModuleMode && (
                <div>
                  {/* Single-track AI cover hero. Populates from
                      `outline.cover` once the fill task's cover worker
                      completes (~30s parallel with section fill). Skip
                      in full-LMS mode — there the per-track covers
                      render inside `FullLmsTracksPanel` cards. Only
                      show during/after STAGE_FILLING so the prompt
                      stage doesn't render an empty grey block. */}
                  {!isFullLmsMode && (stage === STAGE_FILLING || outline?.cover?.url) && (
                    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg mb-4 bg-slate-100">
                      {outline?.cover?.url ? (
                        <img
                          src={outline.cover.url}
                          alt={outlineName}
                          className="absolute inset-0 w-full h-full object-cover"
                          draggable={false}
                          onError={(e) => {
                            // Hide a broken/expired signed URL; the
                            // bg-slate-100 of the parent shows through.
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-slate-200 to-slate-100 animate-pulse" />
                      )}
                    </div>
                  )}
                  {stage === STAGE_OUTLINE && !isOutlining ? (
                    <input
                      type="text"
                      value={outlineName}
                      onChange={(e) => {
                        setOutlineName(e.target.value);
                        setOutline((prev) => ({ ...prev, name: e.target.value }));
                      }}
                      className="w-full text-2xl font-bold text-slate-900 bg-transparent border-0 border-b border-slate-200 focus:border-violet-500 focus:outline-none py-1"
                    />
                  ) : (
                    <h1 className="text-2xl font-bold text-slate-900 min-h-[2.25rem] flex items-center">
                      {outlineName || (
                        // Title arrives with the program event (~1-3s
                        // after Generate, after RAG runs). Spinner fills
                        // the gap so the page doesn't render an empty
                        // heading shell.
                        <span className="w-5 h-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                      )}
                    </h1>
                  )}
                  <p className="text-sm text-slate-500 mt-1">
                    {isFullLmsMode && (
                      <>{(outline?.tracks?.length || 0)} tracks · </>
                    )}
                    {outline?.modules.length || 0} modules · {allSections.length} sections · {swipeDeckCount} swipe decks · {quizCount} quizzes
                  </p>
                </div>
              )}

              {isModuleMode && (
                <div>
                  {stage === STAGE_OUTLINE && !isOutlining && outline?.modules[0] ? (
                    <input
                      type="text"
                      value={outline.modules[0].name}
                      onChange={(e) => handleRenameModule(outline.modules[0].uuid, e.target.value)}
                      className="w-full text-2xl font-bold text-slate-900 bg-transparent border-0 border-b border-slate-200 focus:border-violet-500 focus:outline-none py-1"
                    />
                  ) : (
                    <h1 className="text-2xl font-bold text-slate-900 min-h-[2.25rem] flex items-center">
                      {outline?.modules[0]?.name || (
                        // Same gap as track mode — module title lands on
                        // the first `module` event after RAG.
                        <span className="w-5 h-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                      )}
                    </h1>
                  )}
                  <p className="text-sm text-slate-500 mt-1">
                    Module · {allSections.length} sections · {swipeDeckCount} swipe decks · {quizCount} quizzes
                  </p>
                </div>
              )}

              {/* Full-LMS Tracks panel — read-only summary of which
                  modules each track references + the AI-suggested role
                  bindings. Slice 4 will replace this with drag-drop;
                  for slice 3 MVP it's purely informational so the
                  manager can see the multi-track structure at a glance.

                  Each track row links to the module registry below by
                  scrolling to the referenced module on click. Module
                  uuids that don't resolve (shouldn't happen post-done
                  but defensive) render as "(missing)". */}
              {isFullLmsMode && (outline?.tracks?.length || 0) > 0 && (
                <FullLmsTracksPanel
                  tracks={outline.tracks}
                  modules={outline.modules}
                  roleNameById={roleNameById}
                />
              )}

              {/* "Modules (N)" header for full_lms — visually mirrors
                  the FullLmsTracksPanel header so the manager sees the
                  registry list as a peer to the tracks list above.
                  Hidden in track/module modes (their existing title +
                  count line at the top of the right pane already
                  conveys this). */}
              {isFullLmsMode && (outline?.modules?.length || 0) > 0 && (
                <div className="flex items-baseline justify-between mt-2 mb-1">
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                    Modules ({outline.modules.length})
                  </h2>
                  <p className="text-xs text-slate-500">
                    The shared registry · each module can be referenced by multiple tracks
                  </p>
                </div>
              )}

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="modules" type="module">
                  {(dropProvided) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      className="space-y-6"
                    >
                      {outline?.modules.map((m, idx) => (
                        <Draggable
                          key={m.uuid}
                          draggableId={`mod-${m.uuid}`}
                          index={idx}
                          // Modules can only be reordered in the outline
                          // stage (no point during fill, content is
                          // tagged to specific modules), never in module
                          // mode (single module), and never in full-LMS
                          // mode (the registry is an AI-planned shared
                          // resource — registry order isn't meaningful;
                          // per-track ordering isn't a manual-edit
                          // surface either). Sections inside remain
                          // draggable in track mode + full_lms mode
                          // both (sections are a learner-progression
                          // surface inside their parent module).
                          isDragDisabled={moduleDragDisabled}
                        >
                          {(dragProvided) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              data-module-uuid={m.uuid}
                              // scroll-margin-top offset matches the
                              // sticky OutlinePromptBar's footprint
                              // (~100px at rest; ~122-126px when the
                              // "Rewriting outline…" indicator is also
                              // showing) so `scrollIntoView` from the
                              // tracks panel lands BELOW the bar
                              // rather than under it. 140px gives the
                              // editing-indicator state safe headroom.
                              style={{ scrollMarginTop: "140px" }}
                            >
                              <ModuleBlock
                                module={m}
                                stage={stage}
                                isModuleMode={isModuleMode}
                                isOutlining={isOutlining}
                                expandedSections={expandedSections}
                                refiningSectionUuid={refiningSectionUuid}
                                refiningModule={refiningModuleUuid === m.uuid}
                                submittingModule={submittingModuleUuid === m.uuid}
                                moduleRefineProgress={
                                  submittingModuleUuid === m.uuid
                                    ? moduleRefineProgress
                                    : null
                                }
                                moduleRefineBusy={Boolean(submittingModuleUuid)}
                                onStartRefineModule={() => handleStartRefineModule(m.uuid)}
                                onCancelRefineModule={handleCancelRefineModule}
                                onSubmitRefineModule={(text) => handleSubmitRefineModule(m.uuid, text)}
                                onToggleExpandSection={handleToggleExpandSection}
                                onStartRefineSection={handleStartRefineSection}
                                onCancelRefineSection={handleCancelRefineSection}
                                onSubmitRefineSection={handleSubmitRefineSection}
                                onRegenerateSection={handleRegenerateSection}
                                onEditSection={handleEditSection}
                                onRemoveSection={handleRemoveSection}
                                onToggleQuiz={handleToggleQuiz}
                                onToggleSwipeDeck={handleToggleSwipeDeck}
                                onRemoveSectionAsset={handleRemoveSectionAsset}
                                onRevertSectionAsset={handleRevertSectionAsset}
                                submittingSectionForUuid={submittingSectionForUuid}
                                refiningSwipeDeckUuid={refiningSwipeDeckUuid}
                                refiningQuizUuid={refiningQuizUuid}
                                submittingSwipeDeckForUuid={submittingSwipeDeckForUuid}
                                submittingQuizForUuid={submittingQuizForUuid}
                                onStartRefineSwipeDeck={handleStartRefineSwipeDeck}
                                onCancelRefineSwipeDeck={handleCancelRefineSwipeDeck}
                                onSubmitRefineSwipeDeck={handleSubmitRefineSwipeDeck}
                                onStartRefineQuiz={handleStartRefineQuiz}
                                onCancelRefineQuiz={handleCancelRefineQuiz}
                                onSubmitRefineQuiz={handleSubmitRefineQuiz}
                                onRenameModule={(name) => handleRenameModule(m.uuid, name)}
                                onRemoveModule={() => handleRemoveModule(m.uuid)}
                                onAddSection={() => handleAddSection(m.uuid)}
                                onRenameSection={handleRenameSection}
                                onEditSectionBrief={handleEditSectionBrief}
                                onPreviewSwipeDeck={handlePreviewSwipeDeck}
                                dragHandleProps={dragProvided.dragHandleProps}
                                // Full-LMS-only: tracks that reference
                                // this module. Read-only signal used by
                                // ModuleBlock to render the "Shared N×"
                                // badge + the affected-tracks list, so
                                // the manager knows what they're
                                // editing when they touch a shared
                                // module. Empty for non-full_lms or
                                // unreferenced modules.
                                containingTracks={
                                  isFullLmsMode
                                    ? (tracksByModuleUuid.get(m.uuid) || [])
                                    : []
                                }
                                // Mirrors the Draggable's
                                // isDragDisabled so ModuleBlock can
                                // suppress its drag-indicator icon
                                // (otherwise a dead handle sits in the
                                // header in full_lms mode).
                                moduleDragDisabled={moduleDragDisabled}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {dropProvided.placeholder}
                      {/* "Add module" is intentionally hidden in
                          full-LMS mode. A manually-added module would
                          be an orphan (no track references it from the
                          UI — there's no track-assignment affordance
                          today), and the backend approval normalizer
                          drops modules with zero track references.
                          So clicking Add → typing a name → Approve
                          would silently lose the new module. Until
                          there's a UI to attach a registry module to
                          one or more tracks, the prompt-edit bar is
                          the supported path: a manager who wants a
                          new module asks the outline editor to add
                          one (and the LLM also wires up the
                          appropriate track references). */}
                      {stage === STAGE_OUTLINE && !isModuleMode && !isFullLmsMode && !isOutlining && (
                        <button
                          className="w-full py-3 border-2 border-dashed border-slate-300 hover:border-violet-400 hover:bg-violet-50 text-slate-500 hover:text-violet-800 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                          onClick={handleAddModule}
                        >
                          <AddIcon style={{ fontSize: 18 }} />
                          Add module
                        </button>
                      )}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {stage === STAGE_OUTLINE && (
                <div className="sticky bottom-0 bg-slate-50 -mx-8 px-8 py-4 border-t border-slate-200 flex justify-end gap-3">
                  <button
                    className="px-5 py-2 border border-slate-300 hover:bg-white text-slate-700 rounded-lg font-medium text-sm"
                    onClick={handleEditPrompt}
                  >
                    Back to prompt
                  </button>
                  {outlineRevertAvailable && (
                    <button
                      className="px-5 py-2 border border-slate-300 hover:bg-white text-slate-700 rounded-lg font-medium text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleRevertLastEdit}
                      disabled={isOutlining}
                    >
                      <UndoIcon style={{ fontSize: 16 }} />
                      Revert last edit
                    </button>
                  )}
                  <button
                    className="px-6 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg font-medium text-sm flex items-center gap-2 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                    onClick={handleApproveOutline}
                    // Block approval until the outline stream finishes.
                    // Approving mid-stream would 409 (job still in
                    // OUTLINING) and strand the studio in STAGE_FILLING
                    // since the recovery path treats non-awaiting
                    // statuses as "past approval" and polls forever.
                    disabled={allSections.length === 0 || isOutlining}
                  >
                    <AutoAwesomeIcon style={{ fontSize: 16 }} />
                    Looks good — build it
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Per-section editor — opens when the user clicks the edit
          icon on a Ready section. Routes Save through the
          payload-update endpoint instead of the real section save,
          so edits live in generated_payload_json until commit. The
          embed-related props (modalDataQuiz / quizzesData / etc.)
          are stubbed AND the embed slash items are hidden via
          hideAssetEmbeds — quizzes / swipe decks are separate payload
          assets managed on the canvas, so an embedded block here
          would commit unresolved. Managers add embeds post-commit
          via the regular section editor instead. */}
      {/* Key on the section uuid so each open gets a FRESH editor instance.
          The studio keeps this modal permanently mounted and reuses it for
          every section; without remounting, the single BlockNote editor
          carries stale internal document state across open/save/close
          cycles, which made reopened sections throw on load (white screen)
          or silently render blank. A fresh editor per section mirrors how
          BlocksPreview creates one editor per expansion — the path that has
          never had this problem. Falls back to "closed" when no section is
          selected so the editor is torn down between edits. */}
      <SectionInputsModal2
        key={editSectionData?.uuid || "closed"}
        open={editSectionOpen}
        handleClose={() => {
          setEditSectionOpen(false);
          setEditSectionData(null);
        }}
        modalEdit={true}
        isLoading={false}
        modalDataSection={
          editSectionData || { uuid: null, name: "", as_json: [], category: "" }
        }
        setModalDataSection={(updater) => {
          setEditSectionData((prev) =>
            typeof updater === "function" ? updater(prev) : updater,
          );
        }}
        post_learning_section={studioPostLearningSection}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        hideCategory={true}
        hideDeleteAndDuplicate={true}
        hideAssetEmbeds={true}
        modalDataQuiz={{}}
        setModalDataQuiz={() => {}}
        default_data_quiz={{}}
        post_learning_quiz={() => {}}
        delete_learning_quiz={() => {}}
        quizzesData={[]}
        sectionsData={[]}
        modulesData={[]}
        // Shared-module warning. Only renders when the section's parent
        // module is referenced by 2+ tracks (computed at edit-open time
        // in handleEditSection). The banner reinforces the
        // module-header badge so the manager doesn't lose context once
        // they're inside the modal.
        extraHeaderBanner={
          (editSectionData?.sharedAcrossTracks?.length || 0) > 1 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <span className="text-base flex-shrink-0">⚠️</span>
              <div className="text-sm text-amber-900">
                <div className="font-semibold mb-0.5">
                  This module is shared across{" "}
                  {editSectionData.sharedAcrossTracks.length} tracks
                </div>
                <div className="text-xs text-amber-800">
                  Changes to this section will affect every track using
                  this module:{" "}
                  <span className="font-medium">
                    {editSectionData.sharedAcrossTracks
                      .map((t) => t.name || "Unnamed track")
                      .join(", ")}
                  </span>
                  .
                </div>
              </div>
            </div>
          ) : null
        }
      />
      <ContentSourcePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        companyId={props.homeCompany}
        title="Add reference materials"
        showFolders
        files={availableFiles}
        folders={availableFolders}
        selectedFileUuids={selectedFiles}
        selectedFolderUuids={selectedFolders}
        onToggleFile={handleToggleFile}
        onToggleFolder={handleToggleFolder}
        modules={props.modulesData || []}
        sections={props.sectionsData || []}
        selectedModuleUuids={selectedLmsModules}
        selectedSectionUuids={selectedLmsSections}
        onToggleModule={handleToggleLmsModule}
        onToggleSection={handleToggleLmsSection}
        onRefreshFiles={loadReferenceFiles}
      />
      {/*
        Portal-mounted swipe deck preview player. Lives at the studio
        level (not per-section) so only one instance is ever in the tree
        and the carousel + framer-motion bundle is shared. Portaled to
        document.body so the fixed-positioned player escapes any
        ancestor with a `transform`.
      */}
      {previewSwipeDeck &&
        ReactDOM.createPortal(
          <React.Suspense
            fallback={
              <div className="fixed inset-0 z-[1400] bg-black/95 flex items-center justify-center">
                <div className="text-white/80 text-sm">Loading…</div>
              </div>
            }
          >
            <SwipeDeckPlayerLazy
              // No real deck row exists yet — we're previewing the
              // generated spec in memory. previewMode + previewDeck
              // bypass the employee fetch path entirely.
              deckUuid={null}
              previewDeck={previewSwipeDeck}
              previewMode
              companyId={props.homeCompany}
              onClose={() => setPreviewSwipeDeck(null)}
            />
          </React.Suspense>,
          document.body,
        )}
    </div>
  );
}

export default TrackGenerationStudio;
